import './doctor.scss';
import React, { useMemo, useEffect, useRef, useState } from 'react';
import AlertModal from '../common/AlertModal';
import { applyRequiredBadges } from '../../utils/requiredBadge';
import { axiosInstance } from '../../utils/axios';
import { useParams, useNavigate } from 'react-router-dom';
import ClinicalInsightPanelV1 from './ClinicalInsightPanelV1';
import DocumentEditorModalV2Mobile from './DocumentEditorModalV2Mobile';
import NextAppointmentModal from './NextAppointmentModal';
import { hiraganaToKatakana } from '../../utils/kanaConverter';
import { groupFromPattern, mapDoctorPrescriptionSetItemToOrderRow, MAX_AS_NEEDED_DAYS } from './prescriptionSetMapping';
import { clearDoctorReviewDraftSnapshot, loadDoctorReviewDraftSnapshot, saveDoctorReviewDraftSnapshot } from '../../lib/doctorReviewDraftStorage';

// 用法①の固定リスト（薬の種類に関係なく全て表示）
const PARENT_GROUPS = [
  { value: 'FREQ_1', label: '分1' },
  { value: 'FREQ_2', label: '分2' },
  { value: 'FREQ_3', label: '分3' },
  { value: 'FREQ_4', label: '分4' },
  { value: 'FREQ_5', label: '分5' },
  { value: 'FREQ_6', label: '分6' },
  { value: 'FREQ_7', label: '分7' },
  { value: 'AS_NEEDED', label: '頓用' },
  { value: 'NASAL', label: '点鼻' },
  { value: 'OPHTHALMIC', label: '点眼' },
  { value: 'INHALATION', label: '吸入' },
  { value: 'PATCH', label: '貼付' },
  { value: 'TOPICAL', label: '塗布' },
  { value: 'GARGLE', label: 'うがい' },
  { value: 'EXTERNAL', label: '外用' },
  { value: 'SUBCUTANEOUS', label: '皮下注射' },
  { value: 'INTRAVENOUS', label: '静脈注射' },
  { value: 'OTHER', label: 'その他' },
];

const dosesFromGroup = (group) => {
  const m = group?.match?.(/^FREQ_(\d)$/);
  return m ? Number(m[1]) : null;
};

const generateLineUuid = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch (_) {
    // noop
  }
  return `line_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const clampOrderDaysInput = (rawDays, { isAsNeeded = false, maxPrescriptionDays = 90 } = {}) => {
  if (rawDays === '' || rawDays == null) return '';
  const parsed = Number(rawDays);
  if (!Number.isFinite(parsed) || parsed <= 0) return '';
  const maxVal = isAsNeeded ? MAX_AS_NEEDED_DAYS : maxPrescriptionDays;
  return String(Math.min(Math.max(parsed, 1), maxVal));
};

// 用量は小数2桁まで自由入力（1.24 / 1.67 等）。0.5刻みは使わない。
const normalizeDoseDraftInput = (raw) =>
  String(raw ?? '')
    .replace(/[．。､，]/g, '.')
    .replace(/[^\d.]/g, '');

const isDoseDraftInput = (raw) => /^[0-9]*\.?[0-9]{0,2}$/.test(raw);

// blur後は number|''。入力中は文字列保持し、送信直前で Number(tablets_per_dose) する。
// 0以下は無効なので空欄扱い（0を 0.01 に丸めない）。
const normalizeDoseNumber = (value) => {
  if (value === '' || value == null) return '';
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return '';
  return Math.round(parsed * 100) / 100;
};

const AI_SECTION_DEFINITIONS = [
  { key: 'subjective', label: 'S', title: '主観的情報', field: 'ai_subjective', editKey: 's' },
  { key: 'objective', label: 'O', title: '客観的情報', field: 'ai_objective', editKey: 'o' },
  { key: 'assessment', label: 'A/P', title: '評価・計画（Assessment & Plan）', field: 'ai_assessment', editKey: 'a' },
];

const EMPTY_AI_RECORD_EDIT = { s: '', o: '', a: '', memo: '' };

const buildAiRecordEditFromRecord = (record) => ({
  s: record?.ai_subjective || '',
  o: record?.ai_objective || '',
  a: record?.ai_assessment || '',
  memo: record?.ai_memo || '',
});

const buildAiRecordEditsMap = (records = []) =>
  (Array.isArray(records) ? records : []).reduce((acc, record) => {
    if (!record?.id) return acc;
    acc[record.id] = buildAiRecordEditFromRecord(record);
    return acc;
  }, {});

const getAiSectionConfirmation = (record, sectionKey) =>
  record?.ai_confirmations?.[sectionKey] || { confirmed: false, confirmed_at: null };

const getAiSectionPersistedValue = (record, sectionKey) => {
  const def = AI_SECTION_DEFINITIONS.find(({ key }) => key === sectionKey);
  if (!def) return '';
  return record?.[def.field] || '';
};

const isAiSectionDirty = (record, sectionKey, currentValue) =>
  getAiSectionPersistedValue(record, sectionKey) !== (currentValue || '');

const hasUnconfirmedAiSection = (record) =>
  AI_SECTION_DEFINITIONS.some(
    ({ key, field }) => record?.[field] && !getAiSectionConfirmation(record, key).confirmed,
  );

/**
 * AI下書き → 公式カルテ(soap_*) の安全マージ
 * - AIが空: 既存soapを消さない
 * - 既存soapが空: AI内容で埋める
 * - 両方あり: 確定済みセクションのみAIで上書き
 */
const mergeAiValueIntoSoapField = (aiValue, existingValue, confirmed) => {
  const ai = (aiValue || '').trim();
  if (!ai) return existingValue || '';
  if (confirmed) return aiValue;
  if (!(existingValue || '').trim()) return aiValue;
  return existingValue || '';
};

const POST_FINISH_STATUSES = new Set([
  'billing_confirmation_pending',
  'payment_pending',
  'delivery_pending',
  'completed',
]);

const isQuestionnaireFileUrl = (value) => typeof value === 'string' && (/^(https?:)?\/\//i.test(value) || value.startsWith('/rails/'));

const extractQuestionnaireAnswerFiles = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    if (value.every((item) => typeof item === 'string' && isQuestionnaireFileUrl(item))) {
      return value;
    }
    return value.flatMap((item) => extractQuestionnaireAnswerFiles(item));
  }

  if (typeof value === 'string') {
    return isQuestionnaireFileUrl(value) ? [value] : [];
  }

  if (typeof value === 'object') {
    if (Array.isArray(value.files)) {
      return extractQuestionnaireAnswerFiles(value.files);
    }
    if (typeof value.url === 'string' || typeof value.file_url === 'string') {
      return [value];
    }
  }

  return [];
};

/* ====== 保険用：薬検索入力 ====== */
const MedicationSearchInput = ({ valueId, valueLabel, onPick, isFirstVisit }) => {
  const [q, setQ] = useState(valueLabel || '');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [frozen, setFrozen] = useState(!!valueLabel);

  useEffect(() => {
    setQ(valueLabel || '');
    setFrozen(!!valueLabel);
  }, [valueLabel, valueId]);

  useEffect(() => {
    if (!focused || frozen) return;
    if (!q || q === valueLabel) { setItems([]); setOpen(false); return; }
    const h = setTimeout(async () => {
      try {
        // ひらがなをカタカナに変換してから検索
        const searchQuery = hiraganaToKatakana(q);
        const res = await axiosInstance.get('/api/medichat_doctor/medications/search', {
          params: { q: searchQuery, limit: 20, page: 1, ...(isFirstVisit ? { first_visit: 1 } : {}) },
          skipGlobalLoading: true,
        });
        const arr = Array.isArray(res?.data?.items) ? res.data.items : [];
        setItems(arr); setOpen(arr.length > 0);
      } catch { setItems([]); setOpen(false); }
    }, 220);
    return () => clearTimeout(h);
  }, [q, focused, frozen, valueLabel, isFirstVisit]);

  const pick = (it) => {
    if (isFirstVisit && it.psychotropic_drugs) return; // ★ 初診ブロック（二重化）
    onPick?.({ 
      id: it.id, 
      name: it.name,
      normalized_amount_per_unit: it.normalized_amount_per_unit,
      medication_unit: it.medication_unit
    });
    setQ(it.name); setItems([]); setOpen(false); setFrozen(true);
  };

  return (
    <div className="mc-relative">
      <input className="basic-info-input" type="text" placeholder="薬品/コードで検索"
        value={q} onChange={(e)=>{ setQ(e.target.value); setFrozen(false); }}
        onFocus={()=>{ setFocused(true); if (!frozen && items.length) setOpen(true); }}
        onBlur={()=>{ setFocused(false); setOpen(false); }}
      />
      {open && (
        <div className="mc-dropdown-panel">
          {items.map(it => {
            const blocked = !!(isFirstVisit && it.psychotropic_drugs);
            return (
              <button key={it.id} type="button" onMouseDown={(e)=>e.preventDefault()}
                onClick={()=>!blocked && pick(it)}
                className="mc-dropdown-item"
                style={{ opacity: blocked ? .5 : 1, cursor: blocked ? 'not-allowed' : 'pointer' }}>
                <div className="mc-dropdown-item__main">{it.name}</div>
                <div className="mc-dropdown-item__meta">
                  {it.code}・{it.in_house_dispensing ? '院内' : '院外'}
                  {it.psychotropic_drugs ? '・向精神薬' : ''}
                  {blocked ? '（初診不可）' : ''}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};


/* ====== 自費用：1行（セレクト＋数量のみ） ====== */
const SelfPayRow = ({ valueId, valueQty, options, onChange, onRemove }) => (
  <div className="mcsp-grid-react mc-row mc-gap-sm" style={{marginBottom:'6px'}}>
    <div className="mcsp-grid-react mc-row mc-w-80">
      <div className="mcsp-field-react mc-w-100" style={{marginRight:'6px'}}>
        <select value={valueId || ''} onChange={e => onChange({ catalog_item_id: e.target.value ? Number(e.target.value) : null })}
         className="mc-select"
         >
          <option value="">選択してください</option>
          {options.map(o => <option key={o.id} value={o.id}>{o.name}（¥{(o.price_yen||0).toLocaleString()}）</option>)}
        </select>
      </div>
      <div className="mcsp-field-react" style={{width:'80px', flexShrink:0}}>
        <input
          type="number"
          min="1"
          value={valueQty || 1}
          onChange={e => onChange({ quantity: Math.max(1, Number(e.target.value) || 1) })}
          className="mc-input"
          style={{width:'100%'}}
          aria-label="数量"
        />
      </div>
    </div>
    <button type="button" className="mcsp-remove-react medichat-remove-btn" onClick={onRemove}>削除</button>
  </div>
);

// 器材行（保険検査・自費検査・針を統一）
const EquipmentRow = ({ row, allOptions, onChange, onRemove }) => {
  const isNeedle = row.catalog_kind === 'needle';
  return (
    <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px'}}>
      <select
        value={row.catalog_item_id || ''}
        onChange={e => {
          const id = e.target.value ? Number(e.target.value) : null;
          let kind = null;
          if (id) {
            if (allOptions.exam_insurance.find(o => o.id === id)) kind = 'exam_insurance';
            else if (allOptions.exam_self_pay.find(o => o.id === id)) kind = 'exam_self_pay';
            else if (allOptions.needles.find(o => o.id === id)) kind = 'needle';
          }
          onChange({ catalog_item_id: id, catalog_kind: kind });
        }}
        className="mc-select"
        style={{flex:1, minWidth:0}}
      >
        <option value="">選択してください</option>
        {allOptions.exam_insurance.length > 0 && (
          <optgroup label="── 検査（保険） ──">
            {allOptions.exam_insurance.map(o => (
              <option key={o.id} value={o.id}>{o.name}（{o.std_points || 0}点）</option>
            ))}
          </optgroup>
        )}
        {allOptions.exam_self_pay.length > 0 && (
          <optgroup label="── 検査（自費） ──">
            {allOptions.exam_self_pay.map(o => (
              <option key={o.id} value={o.id}>{o.name}（¥{(o.total_yen || 0).toLocaleString()}）</option>
            ))}
          </optgroup>
        )}
        {allOptions.needles.length > 0 && (
          <optgroup label="── 針・器材 ──">
            {allOptions.needles.map(o => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </optgroup>
        )}
      </select>
      {isNeedle && (
        <>
          <input
            type="number"
            min="1"
            value={row.quantity || 1}
            onChange={e => onChange({ quantity: Math.max(1, Number(e.target.value || 1)) })}
            style={{width:'56px', textAlign:'center', padding:'8px 4px', border:'1px solid #d1d5db', borderRadius:'6px', fontSize:'15px', flexShrink:0}}
          />
          <span style={{fontSize:'13px', color:'#6b7280', flexShrink:0}}>本</span>
        </>
      )}
      <button type="button" className="mcsp-remove-react medichat-remove-btn" onClick={onRemove} style={{flexShrink:0}}>削除</button>
    </div>
  );
};


// ★ 書類行：自費 or 保険（points）を自動表示
const DocumentRow = ({ valueId, valueQty, valueAddressee, options, onChange, onRemove, coverageKind }) => {
  const selected = options.find(o => Number(o.id) === Number(valueId));
  const filtered = options.filter(o => (o.kind === 'insurance' ? coverageKind === 'insurance' : true));
  return (
    <div className="mcsp-row-react">
      <div className="mcsp-grid-react mc-row mc-row--end mc-gap-sm">
        <div className="mcsp-field-react" style={{flex:2}}>
          <label className="form-label-modern required">書類</label>
          <select
            value={valueId || ''}
            onChange={e => onChange({ catalog_item_id: e.target.value ? Number(e.target.value) : null })}
            className="mc-select"
            required
          >
            <option value="">選択してください</option>
            {filtered.map(o => (
              <option key={o.id} value={o.id}>
                {o.name}（{o.kind === 'insurance' ? `${o.points}点` : `¥${(o.price_yen||0).toLocaleString()}`}）
              </option>
            ))}
          </select>
        </div>

        <div className="mcsp-field-react" style={{flex:'0 0 120px'}}>
          <label className="form-label-modern required">数量</label>
          <input type="number" min="1" step="1" value={valueQty ?? 1}
                 onChange={e => onChange({ quantity: Math.max(1, Number(e.target.value || 1)) })}
                 required
          />
        </div>

        {/* 宛名あり（保険）選択時のみ宛名欄を表示 */}
        {selected?.code === 'DOC_INFO_ADDR' && (
          <div className="mcsp-field-react" style={{flex:2}}>
            <label className="form-label-modern required">宛名</label>
            <input type="text" placeholder="紹介先・医療機関名など"
                   value={valueAddressee || ''}
                   onChange={e => onChange({ addressee: e.target.value })}
                   required
            />
          </div>
        )}
      </div>

      <button type="button" className="mcsp-remove-react medichat-remove-btn" onClick={onRemove}>削除</button>
    </div>
  );
};


/* 画像/ファイル閲覧モーダル */
const FileViewerModal = ({ open, onClose, files, title }) => {
  if (!open) return null;

  const fileList = Array.isArray(files) ? files : (files ? [files] : []);

  return (
    <div className="modal_overlay" onClick={onClose} style={{zIndex: 9999}}>
      <div className="modal_content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '90%', width: '600px', maxHeight: '90vh', overflow: 'auto'}}>
        <button className="close_button" onClick={onClose}>✖︎</button>
        <p className="modal_title">{title || 'ファイル閲覧'}</p>
        <div style={{marginTop: '20px'}}>
          {fileList.length === 0 ? (
            <div style={{textAlign: 'center', padding: '20px', color: '#64748b'}}>ファイルがありません</div>
          ) : (
            <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
              {fileList.map((file, idx) => {
                const url = typeof file === 'string' ? file : (file?.url || file?.file_url);
                const isImage = url && (/\.(jpg|jpeg|png|gif|webp|heic|heif|bmp|svg)(\?|$)/i.test(url) || /\/(blobs|representations)\//.test(url));

                return (
                  <div key={idx} style={{border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden'}}>
                    {isImage ? (
                      <a href={url} target="_blank" rel="noreferrer">
                        <img src={url} alt={`ファイル ${idx + 1}`} style={{width: '100%', height: 'auto', display: 'block'}} />
                      </a>
                    ) : (
                      <div style={{padding: '16px', textAlign: 'center'}}>
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px 20px',
                            background: '#3b82f6',
                            color: 'white',
                            borderRadius: '8px',
                            textDecoration: 'none',
                            fontWeight: '600'
                          }}
                        >
                          📄 ファイルを開く
                        </a>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PostConsultReviewPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const urlSessionId = id ? Number(id) : null;
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [viewerFiles, setViewerFiles] = useState([]);
  const [activeAiTab, setActiveAiTab] = useState(0); // AIカルテタブの選択状態
  const [viewerTitle, setViewerTitle] = useState('');

  const [list, setList] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [doctorProfile, setDoctorProfile] = useState(null);
  const [openDates, setOpenDates] = useState(new Set());
  const [prescriptionHistory, setPrescriptionHistory] = useState(null);
  const [loadingPrescriptionHistory, setLoadingPrescriptionHistory] = useState(false);

  const [patterns, setPatterns] = useState([]);
  const [diseaseMaster, setDiseaseMaster] = useState([]); // [{id,name,insurance_covered}]
  const [selfPayMeds, setSelfPayMeds] = useState([]);
  const [docMaster, setDocMaster] = useState([]); // ← 書類マスター
  const [docRows, setDocRows] = useState([]);     // ← 追加した書類（重複は避ける運用）
  const [symptomMaster, setSymptomMaster] = useState([]); // [{id, symptom_name, category}]
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [docModalTarget, setDocModalTarget] = useState({ idx: null, selectedDoc: null });
  const [nextApptModalOpen, setNextApptModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [prescriptionSetModalOpen, setPrescriptionSetModalOpen] = useState(false);
  const [prescriptionSetList, setPrescriptionSetList] = useState([]);
  const [prescriptionSetLoading, setPrescriptionSetLoading] = useState(false);
  const [selectedPrescriptionSetId, setSelectedPrescriptionSetId] = useState('');
  const [prescriptionSetFeatureEnabled, setPrescriptionSetFeatureEnabled] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNote, setCancelNote] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [adoptingAiRecordId, setAdoptingAiRecordId] = useState(null);
  const [confirmingAiSection, setConfirmingAiSection] = useState(null);
  const [confirmingAllAi, setConfirmingAllAi] = useState(false);
  const [aiRecordEdits, setAiRecordEdits] = useState({});
  const [sendingEmergencyMeeting, setSendingEmergencyMeeting] = useState(false);
  const [expandedQaSessions, setExpandedQaSessions] = useState(new Set()); // 展開された問診セッションのIDセット
  const [karakenQaOpen, setKarakenQaOpen] = useState(false); // からけん時代の問診トグル

  // 一覧タブ state（本日/全て）
  const [reviewListTab, setReviewListTab] = useState('today'); // 'today' | 'all'

  // ナビゲーションタブ state
  const [activeMainTab, setActiveMainTab] = useState('info'); // 'info' | 'karte' | 'order'
  const [activeInfoSubTab, setActiveInfoSubTab] = useState('questionnaire'); // 'questionnaire' | 'profile' | 'files' | 'history'
  const [activeKarteSubTab, setActiveKarteSubTab] = useState('manual'); // 'manual' | 'ai'
  const [fabOpen, setFabOpen] = useState(false); // フローティングアクションボタンの開閉
  const [doctorBotOpen, setDoctorBotOpen] = useState(false);
  const [doctorBotInput, setDoctorBotInput] = useState('');
  const [doctorBotLoading, setDoctorBotLoading] = useState(false);
  const [doctorBotMessages, setDoctorBotMessages] = useState([
    {
      role: 'bot',
      text: '診察や治療に関して気になる点があれば質問してください。問診内容や患者情報を踏まえて回答します。',
    },
  ]);
  const doctorBotThreadRef = useRef(null);

  // 傷病名関連のstate
  const [patientDiagnoses, setPatientDiagnoses] = useState([]);
  const [syncingOrca, setSyncingOrca] = useState(false);

  const [rec, setRec] = useState({ soap_subjective:'', soap_objective:'', soap_assessment_plan:'', doctor_notes:'', prescriptions:[] });
  const [alertModal, setAlertModal] = useState({ open: false, title: '', message: '', onCloseCallback: null, type: 'alert', onConfirm: null, iconType: null });

  // アラート表示ヘルパー
  const showAlert = (title, message, iconType = null, onCloseCallback = null) => {
    setAlertModal({ open: true, title, message, type: 'alert', onConfirm: null, iconType, onCloseCallback });
  };

  // 確認ダイアログ表示ヘルパー
  const showConfirm = (title, message, onConfirm, iconType = 'confirm') => {
    setAlertModal({ open: true, title, message, type: 'confirm', onConfirm, iconType, onCloseCallback: null });
  };

  // 保険UI
  const [orderRows, setOrderRows] = useState([]);
  // 自費UI
  const [selfPayRows, setSelfPayRows] = useState([]);
  const [documentRows, setDocumentRows] = useState([]); 

  const [submitting, setSubmitting] = useState(false);
  const submitInFlightRef = useRef(false);
  const draftInFlightRef = useRef(false);
  const [primaryDiseaseId, setPrimaryDiseaseId] = useState(null);
  const [examMasters, setExamMasters] = useState({ exam_insurance: [], exam_self_pay: [], needles: [] });
  const [equipmentRows, setEquipmentRows] = useState([]);

  const personalPrescriptionSetList = useMemo(
    () => prescriptionSetList.filter((item) => item?.set_scope !== 'shared'),
    [prescriptionSetList]
  );
  const sharedPrescriptionSetList = useMemo(
    () => prescriptionSetList.filter((item) => item?.set_scope === 'shared'),
    [prescriptionSetList]
  );

  // 「選択中の主疾患」からリアルタイム coverage を決定
  const selectedDisease = useMemo(
    () => diseaseMaster.find(d => d.id === Number(primaryDiseaseId)) || null,
    [diseaseMaster, primaryDiseaseId]
  );
  const coverageKindLive = selectedDisease ? (selectedDisease.insurance_covered ? 'insurance' : 'self_pay') : (detail?.coverage_kind || 'insurance');

  const patternIndex = useMemo(() => {
    const m = new Map(); patterns.forEach(p => m.set(p.id, p)); return m;
  }, [patterns]);

  useEffect(() => {
    let mounted = true;
    const loadPrescriptionSetFeatureStatus = async () => {
      try {
        const res = await axiosInstance.get('/api/medichat_doctor/prescription_sets/feature_status');
        if (!mounted) return;
        setPrescriptionSetFeatureEnabled(!!res?.data?.enabled);
      } catch (_) {
        if (!mounted) return;
        // 安全側: 初期取得失敗時は利用不可として扱う
        setPrescriptionSetFeatureEnabled(false);
      }
    };
    loadPrescriptionSetFeatureStatus();
    return () => { mounted = false; };
  }, []);

  const buildDraftSnapshot = ({ serverPayloadRevision, serverPayloadHash } = {}) => ({
    sessionId: selectedSessionId,
    record: rec,
    primaryDiseaseId,
    orderRows,
    selfPayRows,
    docRows,
    equipmentRows,
    aiRecordEdits,
    serverPayloadRevision: serverPayloadRevision ?? detail?.payload_revision ?? null,
    serverPayloadHash: serverPayloadHash ?? detail?.last_payload_hash ?? null,
  });

  const getAiRecordEdit = (record) => {
    if (!record?.id) return EMPTY_AI_RECORD_EDIT;
    return aiRecordEdits[record.id] || buildAiRecordEditFromRecord(record);
  };

  const patchAiRecordEdit = (record, patch) => {
    if (!record?.id || detail?.status === 'cancelled') return;
    setAiRecordEdits((prev) => ({
      ...prev,
      [record.id]: {
        ...(prev[record.id] || buildAiRecordEditFromRecord(record)),
        ...patch,
      },
    }));
  };

  /* masters */
  const loadMasters = async () => {
    const [pats, syms] = await Promise.all([
      axiosInstance.get('/api/medichat_doctor/masters/dosage_patterns'),
            axiosInstance.get('/api/medichat_doctor/masters/symptoms'),
      ]);
    setPatterns(pats.data.items || []);

    setSymptomMaster(Array.isArray(syms?.data?.symptoms) ? syms.data.symptoms.map(s => ({
            id: Number(s.id),
            name: s.symptom_name,
            category: s.category || ''
          })) : []);
    try {
      const d = await axiosInstance.get('/api/medichat_doctor/masters/diseases'); // ← id, name, insurance_covered を返す想定
      const items = Array.isArray(d.data?.items) ? d.data.items
                   : Array.isArray(d.data?.diseases) ? d.data.diseases : [];
      setDiseaseMaster(items.map(x => ({ id:Number(x.id), name:x.name||x.disease_name, insurance_covered:!!x.insurance_covered })));
    } catch { setDiseaseMaster([]); }
    try {
      const ex = await axiosInstance.get('/api/medichat_doctor/post_consult_reviews/examinations_master');
      const normalize = arr => (Array.isArray(arr) ? arr : []).map(x => ({
        id:         Number(x.id),
        code:       String(x.code || ''),
        name:       String(x.name || ''),
        catalog_kind: String(x.catalog_kind || ''),
        kind:       String(x.kind || 'self_pay'),
        std_points: Number(x.std_points ?? 0),
        total_yen:  Number(x.total_yen ?? 0),
        price_yen:  Number(x.total_yen ?? 0),
      }));
      setExamMasters({
        exam_insurance: normalize(ex.data?.exam_insurance),
        exam_self_pay:  normalize(ex.data?.exam_self_pay),
        needles:        normalize(ex.data?.needles),
      });
    } catch { setExamMasters({ exam_insurance: [], exam_self_pay: [], needles: [] }); }    
    try {
      const sp = await axiosInstance.get('/api/medichat_doctor/masters/self_pay_medications');
      setSelfPayMeds(Array.isArray(sp.data?.items) ? sp.data.items : []);
    } catch (e) {
      console.error('❌ 自費薬マスター取得失敗:', e);
      setSelfPayMeds([]);
      showAlert('エラー', '自費薬マスターの取得に失敗しました。ページを再読み込みしてください。', 'error');
    }
    
    try {
      // 管理側で用意しているエンドポイントを使う
      const docs = await axiosInstance.get('/api/medichat_doctor/post_consult_reviews/documents_master');
      console.log('📄 書類マスターAPI応答:', docs.data);
      const arr = Array.isArray(docs.data?.items) ? docs.data.items : [];
      console.log('📄 書類マスター件数:', arr.length);
      
      if (arr.length === 0) {
        console.warn('⚠️ 書類マスターが0件です。データベースを確認してください。');
      }
      
      // kind を 'insurance' / 'self_pay' に正規化しておくと後で絞り込みに使える
      const mapped = arr.map(x => ({
        id: Number(x.id),
        code: String(x.code || ''),
        name: String(x.name || ''),
        kind: x.kind === 'insurance' ? 'insurance' : 'self_pay',
        points: Number(x.points || 0),
        price_yen: Number(x.price_yen ?? x.total_yen ?? 0),
        total_yen: Number(x.total_yen ?? x.price_yen ?? 0), // 表示側の互換
      }));
      
      console.log('📄 書類マスター（正規化後）:', mapped);
      setDocMaster(mapped);
    } catch (e) {
      console.error('❌ 書類マスター取得失敗:', e);
      console.error('エラー詳細:', e.response?.data || e.message);
      setDocMaster([]);
      showAlert('エラー', '書類マスターの取得に失敗しました。ページを再読み込みしてください。', 'error');
    }
  };

   const symptomById = useMemo(() => {
        const m = new Map();
        (symptomMaster || []).forEach(s => m.set(Number(s.id), s.name || s.symptom_name || ''));
        return m;
      }, [symptomMaster]);

  const loadDoctorProfile = async () => {
    try {
      const { data } = await axiosInstance.get('/api/medichat_doctor/profile');
      setDoctorProfile(data.doctor || null);
    } catch (e) {
      console.error('医師プロフィール取得エラー:', e);
      showAlert('エラー', '医師プロフィールの取得に失敗しました。', 'error');
    }
  };

  const loadList = async () => {
    setLoadingList(true);
    try {
      const { data } = await axiosInstance.get('/api/medichat_doctor/post_consult_reviews');
      setList(data.sessions || []);
      
      // 今日の日付を開いた状態で初期化
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      setOpenDates(new Set([todayStr]));
    } finally { setLoadingList(false); }
  };

  // 日付ごとにグループ化（今日の診察を最上位に）
  const sessionsByDate = useMemo(() => {
    const groups = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    
    list.forEach(s => {
      // 予約診察はscheduled_at、即時診察はstarted_at、フォールバックでcreated_at
      const dateTime = new Date(s.scheduled_at || s.started_at || s.created_at);
      const dateStr = `${dateTime.getFullYear()}-${String(dateTime.getMonth() + 1).padStart(2, '0')}-${String(dateTime.getDate()).padStart(2, '0')}`;
      
      if (!groups[dateStr]) {
        groups[dateStr] = {
          dateStr,
          dateObj: new Date(dateStr),
          isToday: dateStr === todayStr,
          sessions: []
        };
      }
      groups[dateStr].sessions.push(s);
    });
    
    // 日付順にソート（今日の診察を最優先）
    return Object.values(groups).sort((a, b) => {
      if (a.isToday) return -1;
      if (b.isToday) return 1;
      return a.dateObj - b.dateObj;
    });
  }, [list]);

   const pickName = (obj) => {
       if (!obj) return '';
     if (typeof obj === 'string') return obj;
       return obj.name || obj.disease_name || obj.symptom_name || '';
     };

     const toSymptomName = (v, symptomById) => {
      if (!v) return '';
      if (typeof v === 'string') return v;
      if (typeof v === 'number') return symptomById.get(v) || '';
      // object
      const byField = v.name || v.symptom_name;
      if (byField) return byField;
      const id = v.id != null ? Number(v.id) : NaN;
      return Number.isFinite(id) ? (symptomById.get(id) || '') : '';
    };
    
     // APIが返すそのままの配列を正規化
     const pickedSymptomNames = useMemo(() => {
      const raw = detail?.selected_symptoms;
      if (!raw) return [];
      const arr = Array.isArray(raw) ? raw : [raw];
      return arr
        .map(v => toSymptomName(v, symptomById))
        .filter(Boolean);
      // 依存を強化：セッションが切り替わる/配列が更新される/辞書が揃うタイミングで必ず再計算
    }, [detail?.session_id, JSON.stringify(detail?.selected_symptoms || []), symptomById]);

         // 主疾患表示名：優先順位 = ①primary_disease_id→マスター照合 → ②患者選択の先頭 → ③ダッシュ
           const diseaseById = React.useMemo(() => {
              const map = new Map();
              (diseaseMaster || []).forEach(d => map.set(Number(d.id), d.name || d.disease_name || ''));
              return map;
            }, [diseaseMaster]);
          
            // 2) 患者が選択した疾患の表示名配列（ID/文字列/オブジェクトすべて吸収）
            const pickedDiseaseNames = React.useMemo(() => {
              const src = Array(detail?.selected_diseases || []);
              return src.map((v) => {
                if (typeof v === 'string') return v;
                if (typeof v === 'number') return diseaseById.get(Number(v)) || '';
                const name = v?.name || v?.disease_name;
                if (name) return name;
                const id = v?.id != null ? Number(v.id) : NaN;
                return Number.isFinite(id) ? (diseaseById.get(id) || '') : '';
              }).filter(Boolean);
            }, [detail?.selected_diseases, diseaseById]);
       
              const primaryDiseaseDisplayName =
                (detail?.primary_disease_id && diseaseById.get(Number(detail.primary_disease_id)))
                || pickedDiseaseNames[0]
                || '';


  const loadDetail = async (sid) => {
    if (!sid) return;
    setLoadingDetail(true);
    try {
      const { data } = await axiosInstance.get(`/api/medichat_doctor/post_consult_reviews/${sid}`);
      const savedDraftSnapshot = loadDoctorReviewDraftSnapshot(sid);
      setDetail(data);
      setRec(data.record || {});
      setAiRecordEdits(buildAiRecordEditsMap(data.ai_consultation_records));
      setActiveAiTab(0);
      setPrimaryDiseaseId(data.primary_disease_id ?? data.selected_diseases?.[0]?.id ?? null);
      // 傷病名データを設定
      setPatientDiagnoses(data.patient_diagnoses || []);

      let medOrders = (data.orders || []).filter(o => !!o.catalog_item_id);

      console.log('📊 診察データ:', {
        consultation_mode: data.consultation_mode,
        coverage_kind: data.coverage_kind,
        existing_orders: medOrders.length,
        do_prescription: data.do_prescription?.length || 0
      });

      // Do処方：既存のオーダーがなく、かつDo処方データがある場合は、Do処方で初期化
      if (medOrders.length === 0 && data.do_prescription && data.do_prescription.length > 0) {
        console.log('✅ Do処方を適用します:', data.do_prescription);
        medOrders = data.do_prescription;
        console.log('💊 Do処方データをmedOrdersに設定しました:', medOrders);
      } else if (data.do_prescription && data.do_prescription.length > 0) {
        console.log('⚠️ Do処方がありますが、既存のオーダーがあるため適用しません');
      }

      // 既存は「現状の coverage_kind」に合わせて片方へ復元（編集開始時点）
      if ((data.coverage_kind || 'insurance') === 'self_pay') {
        setOrderRows([]);
        setSelfPayRows(medOrders.map(o => ({ catalog_item_id:o.catalog_item_id, quantity:o.quantity || 1 })));
      } else {
        const rows = medOrders.map((o) => {
          const pat = o.dosage_pattern_id ? patternIndex.get(o.dosage_pattern_id) : null;
          const usage_group = pat ? groupFromPattern(pat) : '';
          return {
            line_uuid: o.line_uuid || generateLineUuid(),
            catalog_item_id: o.catalog_item_id || null,
            catalog_item_label: o.item_name || o.catalog_item_name || '',
            usage_group,
            dosage_pattern_id: o.dosage_pattern_id || null,
            tablets_per_dose: o.tablets_per_dose || '',
            doses_per_day: o.doses_per_day || null,
            days: clampOrderDaysInput(o.days, { isAsNeeded: usage_group === 'AS_NEEDED', maxPrescriptionDays: daysMax }),
            memo: o.memo || '',
            body_part: o.body_part || '',
          };
        });
        setOrderRows(rows); setSelfPayRows([]);
      }

      // === 書類オーダーの復元 ===
      const existingDocOrders = data.document_orders || [];
      const treatmentPlanRemovedKey = `mcTreatmentPlanRemoved_${sid}`;
      const treatmentPlanExplicitlyRemoved =
        typeof window !== 'undefined' &&
        sessionStorage.getItem(treatmentPlanRemovedKey) === 'true';
      if (existingDocOrders.length > 0) {
        // 既存の書類オーダーがある場合、それを復元（field_valuesを含む）
        console.log('📄 既存書類オーダーを復元:', existingDocOrders.length, '件');
        setDocRows(existingDocOrders.map(doc => ({
          catalog_item_id: doc.catalog_item_id,
          field_values: doc.field_values || {}
        })));
      } else if (data.needs_treatment_plan && data.treatment_plan_catalog_item && !treatmentPlanExplicitlyRemoved) {
        // 既存の書類オーダーがなく、療養計画書が必要な場合のみ新規追加
        console.log('✅ 療養計画書をデフォルトで追加します');
        setDocRows([{ 
          catalog_item_id: data.treatment_plan_catalog_item.id,
          field_values: {}
        }]);
      } else {
        setDocRows([]);
      }
      
      // === 検査オーダーの復元（全器材をequipmentRowsに統合） ===
      const existingExamOrders = data.examination_orders || [];
      if (existingExamOrders.length > 0) {
        setEquipmentRows(existingExamOrders.map(e => ({
          catalog_item_id: e.catalog_item_id,
          catalog_kind: e.catalog_kind,
          quantity: e.quantity || 1
        })));
      } else {
        setEquipmentRows([]);
      }

      // 処方歴を読み込む
      if (
        savedDraftSnapshot &&
        Number(savedDraftSnapshot.sessionId) === Number(sid) &&
        (
          (
            savedDraftSnapshot.serverPayloadRevision != null &&
            Number(savedDraftSnapshot.serverPayloadRevision) === Number(data.payload_revision)
          ) ||
          (
            savedDraftSnapshot.serverPayloadHash &&
            savedDraftSnapshot.serverPayloadHash === data.last_payload_hash
          )
        )
      ) {
        setRec(savedDraftSnapshot.record || {});
        setPrimaryDiseaseId(savedDraftSnapshot.primaryDiseaseId ?? data.primary_disease_id ?? data.selected_diseases?.[0]?.id ?? null);
        setOrderRows((savedDraftSnapshot.orderRows || []).map((row) => ({
          ...row,
          days: clampOrderDaysInput(row.days, { isAsNeeded: row.usage_group === 'AS_NEEDED', maxPrescriptionDays: daysMax }),
        })));
        setSelfPayRows(savedDraftSnapshot.selfPayRows || []);
        setDocRows(savedDraftSnapshot.docRows || []);
        setEquipmentRows(savedDraftSnapshot.equipmentRows || []);
        if (savedDraftSnapshot.aiRecordEdits) {
          setAiRecordEdits(savedDraftSnapshot.aiRecordEdits);
        }
      }

      loadPrescriptionHistory(sid);
    } finally { setLoadingDetail(false); }
  };

  const confirmAiSection = async (recordId, section) => {
    if (!selectedSessionId || !recordId) return;

    setConfirmingAiSection(`${recordId}-${section}`);
    try {
      const { data } = await axiosInstance.post(
        `/api/medichat_doctor/post_consult_reviews/${selectedSessionId}/confirm_ai_section`,
        {
          ai_consultation_record_id: recordId,
          section,
        },
      );
      await loadDetail(selectedSessionId);
      showAlert('成功', data?.message || 'AI下書きを確定しました', 'success');
    } catch (e) {
      showAlert('エラー', e?.response?.data?.error || e.message || '確定に失敗しました', 'error');
    } finally {
      setConfirmingAiSection(null);
    }
  };

  const confirmAllAiSections = async (recordId) => {
    if (!selectedSessionId || !recordId) return;

    setConfirmingAllAi(true);
    try {
      const { data } = await axiosInstance.post(
        `/api/medichat_doctor/post_consult_reviews/${selectedSessionId}/confirm_all_ai_sections`,
        {
          ai_consultation_record_id: recordId,
        },
      );
      await loadDetail(selectedSessionId);
      showAlert('成功', data?.message || 'AI下書きを確定しました', 'success');
    } catch (e) {
      showAlert('エラー', e?.response?.data?.error || e.message || '確定に失敗しました', 'error');
    } finally {
      setConfirmingAllAi(false);
    }
  };

  const loadPrescriptionHistory = async (sessionId) => {
    setLoadingPrescriptionHistory(true);
    try {
      console.log('処方歴を読み込みます:', sessionId);
      const { data } = await axiosInstance.get(`/api/medichat_doctor/post_consult_reviews/${sessionId}/prescription_history`, { skipGlobalLoading: true });
      console.log('処方歴データ:', data);
      setPrescriptionHistory(data);
    } catch (e) {
      console.error('❌ 処方歴の取得に失敗:', e);
      console.error('エラー詳細:', e.response?.data || e.message);
      showAlert('エラー', e?.response?.data?.error || e.message || '処方歴の取得に失敗しました', 'error');
      setPrescriptionHistory({ primary: [], other: [] });
    } finally {
      setLoadingPrescriptionHistory(false);
    }
  };

  useEffect(() => { loadMasters(); loadList(); loadDoctorProfile(); }, []);
  useEffect(() => {
    if (urlSessionId) { setSelectedSessionId(urlSessionId); loadDetail(urlSessionId); }
    else { setSelectedSessionId(null); setDetail(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSessionId]);

  useEffect(() => {
    setDoctorBotOpen(false);
    setDoctorBotInput('');
    setDoctorBotLoading(false);
    setDoctorBotMessages([
      {
        role: 'bot',
        text: '診察や治療に関して気になる点があれば質問してください。問診内容や患者情報を踏まえて回答します。',
      },
    ]);
  }, [selectedSessionId]);

  useEffect(() => {
    if (!doctorBotThreadRef.current) return;
    doctorBotThreadRef.current.scrollTop = doctorBotThreadRef.current.scrollHeight;
  }, [doctorBotMessages, doctorBotLoading]);

  // detailが読み込まれたら、現在のセッションを自動展開
  useEffect(() => {
    if (detail?.session_id) {
      setExpandedQaSessions(new Set([detail.session_id]));
    }
  }, [detail?.session_id]);

  // 表示されたフォームのラベルに「※必須」バッジを追加（必要項目のみrequiredを付けた場合に反映）
  useEffect(() => {
    try { applyRequiredBadges('.medichat-content'); } catch {}
  }, [detail, orderRows, selfPayRows, docRows, equipmentRows]);

  /* 主疾患の即時切替 → 薬フォームをクリア＆初期行 */
  useEffect(() => {
    if (!detail) return;
    // どちらかが0→もう一方に切替時に混在しないようクリア
    if (coverageKindLive === 'self_pay') {
      setOrderRows([]); setEquipmentRows([]); if (selfPayRows.length === 0) setSelfPayRows([{ catalog_item_id:null, quantity:1 }]);
    } else {
      setSelfPayRows([]); setEquipmentRows([]); if (orderRows.length === 0) setOrderRows([{
        line_uuid: generateLineUuid(),
        catalog_item_id:null, catalog_item_label:'', usage_group:'', dosage_pattern_id:null, tablets_per_dose:'', days:'', memo:'', body_part:'', normalized_amount_per_unit:1, medication_unit:'錠'
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverageKindLive]);

  useEffect(() => {
    setDocRows(rows => rows.filter((row) => {
      const item = docMaster.find(doc => Number(doc.id) === Number(row.catalog_item_id));
      if (!item) return false;
      if (item.code === 'LIFESTYLE_DOCUMENT') return coverageKindLive === 'insurance';
      return item.kind === coverageKindLive;
    }));
  }, [coverageKindLive, docMaster]);

  /* 今回の問診セッション（is_current）をデータ取得後に自動展開 */
  useEffect(() => {
    if (!detail?.questionnaire_answers_by_session) return;
    const currentSession = detail.questionnaire_answers_by_session.find(s => s.is_current);
    if (currentSession) {
      setExpandedQaSessions(prev => {
        const next = new Set(prev);
        next.add(currentSession.session_id);
        return next;
      });
    }
  }, [detail]);

  /* タブ切り替え時にスクロール位置をトップにリセット */
  const bodyRef = React.useRef(null);
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = 0;
    }
  }, [activeMainTab]);
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = 0;
    }
  }, [activeInfoSubTab, activeKarteSubTab]);

  /* 行操作（保険） */
  const addOrderRow = () => setOrderRows(r => [...r, { line_uuid: generateLineUuid(), catalog_item_id:null, catalog_item_label:'', usage_group:'', dosage_pattern_id:null, tablets_per_dose:'', days:'', memo:'', body_part:'', normalized_amount_per_unit:1, medication_unit:'錠' }]);
  const applyDoPrescription = () => {
    if (!Array.isArray(detail?.do_prescription) || detail.do_prescription.length === 0) return;
    const confirmed = window.confirm('現在の薬オーダーをDo処方候補で置き換えますか？');
    if (!confirmed) return;
    const rows = detail.do_prescription.map((o) => {
      const pat = o.dosage_pattern_id ? patternIndex.get(o.dosage_pattern_id) : null;
      const usage_group = pat ? groupFromPattern(pat) : '';
      return {
        line_uuid: o.line_uuid || generateLineUuid(),
        catalog_item_id: o.catalog_item_id || null,
        catalog_item_label: o.item_name || o.catalog_item_name || '',
        usage_group,
        dosage_pattern_id: o.dosage_pattern_id || null,
        tablets_per_dose: o.tablets_per_dose || '',
        doses_per_day: o.doses_per_day || null,
        days: clampOrderDaysInput(o.days, { isAsNeeded: usage_group === 'AS_NEEDED', maxPrescriptionDays: daysMax }),
        memo: o.memo || '',
        body_part: o.body_part || '',
        as_needed: o.as_needed === true,
      };
    });
    setOrderRows(rows);
    setSelfPayRows([]);
  };

  const openPrescriptionSetModal = async () => {
    if (prescriptionSetFeatureEnabled !== true) {
      showAlert('処方セット', '処方セット機能は現在無効です');
      return;
    }
    setSelectedPrescriptionSetId('');
    setPrescriptionSetModalOpen(true);
    setPrescriptionSetLoading(true);
    try {
      const listRes = await axiosInstance.get('/api/medichat_doctor/prescription_sets', { skipGlobalLoading: true });
      const personalItems = Array.isArray(listRes?.data?.personal_items) ? listRes.data.personal_items : [];
      const sharedItems = Array.isArray(listRes?.data?.shared_items) ? listRes.data.shared_items : [];
      const fallbackItems = Array.isArray(listRes?.data?.items) ? listRes.data.items : [];
      const sets = personalItems.length > 0 || sharedItems.length > 0
        ? [ ...personalItems, ...sharedItems ]
        : fallbackItems;
      setPrescriptionSetList(sets);
      if (sets.length === 0) {
        showAlert('処方セット', '利用可能な処方セットがありません。処方セット管理画面から作成してください。');
      }
    } catch (e) {
      if (e?.response?.data?.error === 'prescription_set_feature_disabled') {
        setPrescriptionSetFeatureEnabled(false);
      }
      showAlert('エラー', e?.response?.data?.error || e.message || '処方セット取得に失敗しました');
      setPrescriptionSetModalOpen(false);
    } finally {
      setPrescriptionSetLoading(false);
    }
  };

  const applyPrescriptionSet = async (setId) => {
    try {
      const detailRes = await axiosInstance.get(`/api/medichat_doctor/prescription_sets/${setId}`, { skipGlobalLoading: true });
      const setItem = detailRes?.data?.item;
      const items = Array.isArray(setItem?.items) ? setItem.items : [];
      if (items.length === 0) {
        showAlert('処方セット', 'このセットには薬が登録されていません');
        return;
      }

      const nextRows = items.map((it) => (
        mapDoctorPrescriptionSetItemToOrderRow({
          item: it,
          patternIndex,
          generateLineUuid,
          maxPrescriptionDays: daysMax,
        })
      ));

      setOrderRows((prev) => [ ...prev, ...nextRows ]);
      setPrescriptionSetModalOpen(false);
    } catch (e) {
      if (e?.response?.data?.error === 'prescription_set_feature_disabled') {
        setPrescriptionSetFeatureEnabled(false);
      }
      showAlert('エラー', e?.response?.data?.error || e.message || '処方セットの適用に失敗しました');
    }
  };
  const addEquipmentRow = () => setEquipmentRows(rows => [...rows, { catalog_item_id: null, catalog_kind: null, quantity: 1 }]);
  const updateEquipmentRow = (idx, patch) => setEquipmentRows(rows => rows.map((r, i) => i === idx ? {...r, ...patch} : r));
  const removeEquipmentRow = (idx) => setEquipmentRows(rows => rows.filter((_, i) => i !== idx));
  const updateOrderRow = (idx, patch) => setOrderRows(rows => rows.map((r,i)=>(i===idx?{...r,...patch}:r)));
  const removeOrderRow = (idx) => setOrderRows(rows => rows.filter((_,i)=>i!==idx));
  // 外用薬・注射薬のグループ（日数を1に固定）
  const EXTERNAL_INJECTION_GROUPS = ['NASAL', 'OPHTHALMIC', 'INHALATION', 'PATCH', 'TOPICAL', 'GARGLE', 'EXTERNAL', 'SUBCUTANEOUS', 'INTRAVENOUS'];
  // 頓服薬も日数フィールドを非表示にする（回数表記のため）
  const HIDE_DAYS_GROUPS = [...EXTERNAL_INJECTION_GROUPS, 'AS_NEEDED'];
  // 使用部位が必須となるグループ（外用薬系、注射系は除く）
  const BODY_PART_REQUIRED_GROUPS = ['NASAL', 'OPHTHALMIC', 'INHALATION', 'PATCH', 'TOPICAL', 'GARGLE', 'EXTERNAL'];
  const onChangeParentGroup = (idx, groupVal) => {
    const patch = {
      usage_group: groupVal || '',
      dosage_pattern_id: null,
      days: clampOrderDaysInput(orderRows[idx]?.days, {
        isAsNeeded: groupVal === 'AS_NEEDED',
        maxPrescriptionDays: daysMax,
      }),
    };
    // 外用薬・注射薬の場合、日数を1に固定（頓服薬は回数として扱うため固定しない）
    if (EXTERNAL_INJECTION_GROUPS.includes(groupVal)) {
      patch.days = '1';
    }
    updateOrderRow(idx, patch);
  };
  const onChangeChildPattern = (idx, patId) => updateOrderRow(idx, { dosage_pattern_id: patId ? Number(patId) : null });

  /* 行操作（自費） */
  const addSelfPayRow = () => setSelfPayRows(r => [...r, { catalog_item_id:null, quantity:1 }]);
  const updateSelfPayRow = (idx, patch) => setSelfPayRows(rows => rows.map((r,i)=>(i===idx?{...r,...patch}:r)));
  const removeSelfPayRow = (idx) => setSelfPayRows(rows => rows.filter((_,i)=>i!==idx));

   const addDocRow = () => setDocRows(rows => [...rows, { catalog_item_id: null }]);
   const updateDocRow = (idx, patch) =>
     setDocRows(rows => rows.map((r,i)=>(i===idx ? { ...r, ...patch } : r)));
   const removeDocRow = (idx) =>
     setDocRows(rows => {
       const target = rows[idx];
       const treatmentPlanId = detail?.treatment_plan_catalog_item?.id;
       if (
         target &&
         treatmentPlanId &&
         Number(target.catalog_item_id) === Number(treatmentPlanId) &&
         selectedSessionId &&
         typeof window !== 'undefined'
       ) {
         sessionStorage.setItem(`mcTreatmentPlanRemoved_${selectedSessionId}`, 'true');
       }
       return rows.filter((_, i) => i !== idx);
     });

  const REQUIRED_DOC_CODES = new Set(['DOC_CERT', 'DOC_INFO_NOADDR', 'DOC_INFO_ADDR', 'LIFESTYLE_DOCUMENT', 'DOC_REF_ADDR', 'DOC_REF_NOADDR', 'DOC_DISABILITY', 'DOC_WORK_CERT']);
  const DOC_FIELD_LABELS = {
    full_name: '氏名',
    birth_date: '生年月日',
    age: '年齢',
    gender: '性別',
    diagnosis_name: '診断名',
    disease_name: '傷病名',
    primary_disease_name: '主病',
    certificate_body_text: '証明書本文',
    height_cm: '身長',
    weight_kg: '体重',
    bmi: 'BMI',
    careplan_target_goal: '目標',
    careplan_target_action: '行動計画',
    addressee: '宛名',
    referral_purpose_text: '紹介目的',
    current_condition: '現在の状態',
    condition_at_retirement: '退職時の状態',
    work_possible_date: '就労可能日',
    insurance_benefit_status: '傷病手当・労災申請の有無',
  };

  const requiredDocFieldsByCode = (code) => {
    switch (code) {
      case 'LIFESTYLE_DOCUMENT':
        return ['full_name', 'birth_date', 'primary_disease_name', 'height_cm', 'weight_kg', 'bmi', 'careplan_target_goal', 'careplan_target_action'];
      case 'DOC_CERT':
        return ['full_name', 'birth_date', 'diagnosis_name', 'certificate_body_text'];
      case 'DOC_INFO_ADDR':
        return ['full_name', 'birth_date', 'age', 'gender', 'disease_name', 'addressee', 'referral_purpose_text'];
      case 'DOC_DISABILITY':
        return ['disease_name'];
      case 'DOC_WORK_CERT':
        return ['disease_name', 'current_condition', 'condition_at_retirement', 'work_possible_date', 'insurance_benefit_status'];
      case 'DOC_INFO_NOADDR':
      default:
        return ['full_name', 'birth_date', 'age', 'gender', 'disease_name', 'referral_purpose_text'];
    }
  };

  const getMissingRequiredDocumentFields = (row) => {
    if (!row?.catalog_item_id) return [];
    const doc = docMaster.find(d => Number(d.id) === Number(row.catalog_item_id));
    const code = (doc?.code || '').toString().toUpperCase();
    if (!REQUIRED_DOC_CODES.has(code)) return [];
    const fv = row.field_values || {};
    const required = requiredDocFieldsByCode(code);
    return required.filter((key) => !fv[key] || String(fv[key]).trim() === '');
  };

  const buildAiRecordPayload = () => {
    const aiRecords = detail?.ai_consultation_records || [];
    const currentAiRecord =
      aiRecords[activeAiTab]
      || detail?.ai_consultation_record
      || aiRecords.find((record) => record?.is_adopted)
      || aiRecords[0]
      || null;

    if (!currentAiRecord) return undefined;

    const edit = getAiRecordEdit(currentAiRecord);
    return {
      ai_subjective: edit.s,
      ai_objective: edit.o,
      ai_assessment: edit.a,
      ai_memo: edit.memo,
    };
  };

  // AI下書きタブ保存時は安全マージで soap_* を組み立て（空上書き・未確定の手動破壊を防ぐ）
  const buildOfficialRecordPayload = () => {
    if (activeKarteSubTab !== 'ai') return rec;

    const aiRecords = detail?.ai_consultation_records || [];
    const currentAiRecord =
      aiRecords[activeAiTab]
      || detail?.ai_consultation_record
      || aiRecords.find((record) => record?.is_adopted)
      || aiRecords[0]
      || null;
    const aiPayload = buildAiRecordPayload();
    if (!currentAiRecord || !aiPayload) return rec;

    return {
      ...rec,
      soap_subjective: mergeAiValueIntoSoapField(
        aiPayload.ai_subjective,
        rec.soap_subjective,
        getAiSectionConfirmation(currentAiRecord, 'subjective').confirmed,
      ),
      soap_objective: mergeAiValueIntoSoapField(
        aiPayload.ai_objective,
        rec.soap_objective,
        getAiSectionConfirmation(currentAiRecord, 'objective').confirmed,
      ),
      soap_assessment_plan: mergeAiValueIntoSoapField(
        aiPayload.ai_assessment,
        rec.soap_assessment_plan,
        getAiSectionConfirmation(currentAiRecord, 'assessment').confirmed,
      ),
    };
  };

  // 診察終了後はカルテ本文のみ更新（オーダー・主疾患は送らない）
  const buildCarteOnlyPayload = () => ({
    base_payload_revision: detail?.payload_revision,
    base_payload_hash: detail?.last_payload_hash || undefined,
    record: buildOfficialRecordPayload(),
    ai_record: buildAiRecordPayload(),
  });

  const buildReviewPayload = () => {
    if (POST_FINISH_STATUSES.has(detail?.status)) {
      return buildCarteOnlyPayload();
    }

    return {
      primary_disease_id: primaryDiseaseId ? Number(primaryDiseaseId) : null,
      base_payload_revision: detail?.payload_revision,
      base_payload_hash: detail?.last_payload_hash || undefined,
      record: buildOfficialRecordPayload(),
      ai_record: buildAiRecordPayload(),
      orders: (coverageKindLive === 'self_pay')
        ? selfPayRows.filter(r => r.catalog_item_id).map(r => ({
            catalog_item_id: Number(r.catalog_item_id),
            quantity: Math.max(1, Number(r.quantity || 1)),
          }))
        : orderRows.filter(r => r.catalog_item_id).map(r => {
            const isExternalOrInjection = EXTERNAL_INJECTION_GROUPS.includes(r.usage_group);
            return {
              line_uuid: r.line_uuid || generateLineUuid(),
              catalog_item_id: Number(r.catalog_item_id),
              dosage_pattern_id: r.dosage_pattern_id ? Number(r.dosage_pattern_id) : null,
              tablets_per_dose: r.tablets_per_dose ? Number(r.tablets_per_dose) : null,
              days: isExternalOrInjection
                ? 1
                : (() => {
                    const clamped = clampOrderDaysInput(r.days, {
                      isAsNeeded: r.usage_group === 'AS_NEEDED',
                      maxPrescriptionDays: daysMax,
                    });
                    return clamped ? Number(clamped) : null;
                  })(),
              memo: r.memo || '',
              body_part: r.body_part || null,
            };
          }),
      documents: docRows
        .filter(r => r.catalog_item_id)
        .map(r => ({
          catalog_item_id: Number(r.catalog_item_id),
          quantity: Math.max(1, Number(r.quantity || 1)),
          field_values: r.field_values || {}
        })),
      examinations: equipmentRows
        .filter(r => r.catalog_item_id)
        .map(r => ({
          catalog_item_id: Number(r.catalog_item_id),
          quantity: r.catalog_kind === 'needle' ? Math.max(1, Number(r.quantity || 1)) : 1
        })),
    };
  };

  const isFirstVisit = (detail?.consultation_mode || rec?.consultation_mode) === 'first_visit';
  const daysMax = isFirstVisit ? 30 : 90;

  /* ORCAから傷病名を同期 */
  const syncDiagnosesFromOrca = async () => {
    if (!selectedSessionId) return;
    
    if (!window.confirm('ORCAに登録されている傷病名をカルテに反映します。\n\n※からけん時代に手入力した傷病名がORCAにある場合、それらがカルテに追加されます。\n\n続行しますか？')) {
      return;
    }
    
    setSyncingOrca(true);
    try {
      const { data } = await axiosInstance.post(`/api/medichat_doctor/post_consult_reviews/${selectedSessionId}/sync_diagnoses_from_orca`);
      
      if (data.success) {
        setPatientDiagnoses(data.diagnoses || []);
        showAlert('ORCA同期完了', `ORCAから ${data.synced_count} 件の傷病名を同期しました。`, 'success');
      } else {
        showAlert('同期エラー', data.error || '不明なエラーが発生しました。', 'error');
      }
    } catch (e) {
      console.error('ORCA同期エラー:', e);
      showAlert('通信エラー', 'ORCAとの通信中にエラーが発生しました。', 'error');
    } finally {
      setSyncingOrca(false);
    }
  };

  /* 送信（主疾患＋カルテ＋薬） */
  const submitAll = async () => {
    if (!selectedSessionId) return;
    if (submitInFlightRef.current || submitting) return;
    if (!canSubmitReview) return;
    
    // 主疾患は常に必須
    if (!primaryDiseaseId) {
      setAlertModal({ open: true, title: '入力エラー', message: '主疾患の選択は必須です。主疾患を選択してください。' });
      return;
    }

    // ★ 未来の予約チェック（1日以上先の場合は確認ダイアログを表示）
    if (detail?.scheduled_at) {
      const scheduledDate = new Date(detail.scheduled_at);
      const now = new Date();
      const oneDayLater = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      
      if (scheduledDate > oneDayLater) {
        const formattedDate = scheduledDate.toLocaleString('ja-JP', { 
          year: 'numeric', month: 'long', day: 'numeric', 
          hour: '2-digit', minute: '2-digit' 
        });
        setAlertModal({
          open: true,
          title: '未来の予約です',
          message: `この予約は ${formattedDate} に予定されています。\n\n診察開始時刻が1日以上先ですが、本当に診察を終了しますか？`,
          type: 'confirm',
          onConfirm: () => {
            setAlertModal({ open: false, title: '', message: '', type: 'alert', onConfirm: null });
            doSubmitAll();
          }
        });
        return;
      }
    }
    
    // 通常の送信処理
    await doSubmitAll();
  };
  
  // 実際の送信処理
  const doSubmitAll = async () => {
    if (!selectedSessionId) return;
    if (submitInFlightRef.current || submitting) return;
    submitInFlightRef.current = true;
    setSubmitting(true);
    
    try {
      // 保険診療の処方バリデーション（薬品名・用法・数量・日数すべて必須）
      // ただし、外用薬・注射薬の場合は日数は不要
      // また、外用薬系（BODY_PART_REQUIRED_GROUPS）は用法の代わりに使用部位またはメモでもOK
      if (coverageKindLive !== 'self_pay') {
        const asNeededOverLimit = orderRows
          .map((row, idx) => ({ ...row, idx }))
          .find((row) => row.catalog_item_id && row.usage_group === 'AS_NEEDED' && Number(row.days || 0) > MAX_AS_NEEDED_DAYS);
        if (asNeededOverLimit) {
          setAlertModal({
            open: true,
            title: '入力エラー',
            message: `処方 ${asNeededOverLimit.idx + 1}行目：頓服の回数は${MAX_AS_NEEDED_DAYS}回分までです。`,
          });
          return;
        }

        const invalidOrders = orderRows
          .map((row, idx) => ({ ...row, idx }))
          .filter(row => row.catalog_item_id) // 薬が選択されている行のみ
          .filter(row => {
            // 外用薬・注射薬の場合は日数不要
            const isExternalOrInjection = EXTERNAL_INJECTION_GROUPS.includes(row.usage_group);
            const isBodyPartRequired = BODY_PART_REQUIRED_GROUPS.includes(row.usage_group);
            
            // 数量は常に必須
            if (!row.tablets_per_dose) return true;
            
            // 外用薬系（点眼、点鼻など）の場合：使用部位またはメモがあれば用法は不要
            if (isBodyPartRequired) {
              const hasBodyPartOrMemo = (row.body_part || '').trim() || (row.memo || '').trim();
              if (hasBodyPartOrMemo) return false; // OK
              // 使用部位もメモもない場合は用法が必要
              return !row.dosage_pattern_id;
            }
            
            if (isExternalOrInjection) {
              // 注射薬等：用法と数量のみ必須
              return !row.dosage_pattern_id;
            }
            // 内服薬等は用法・数量・日数すべて必須
            return !row.dosage_pattern_id || !row.days;
          });
        
        if (invalidOrders.length > 0) {
          const firstInvalid = invalidOrders[0];
          const isExternalOrInjection = EXTERNAL_INJECTION_GROUPS.includes(firstInvalid.usage_group);
          const isBodyPartRequired = BODY_PART_REQUIRED_GROUPS.includes(firstInvalid.usage_group);
          const missing = [];
          
          if (isBodyPartRequired) {
            // 外用薬系：使用部位またはメモが必要
            const hasBodyPartOrMemo = (firstInvalid.body_part || '').trim() || (firstInvalid.memo || '').trim();
            if (!hasBodyPartOrMemo && !firstInvalid.dosage_pattern_id) {
              missing.push('使用部位（または用法）');
            }
          } else if (!firstInvalid.dosage_pattern_id) {
            missing.push('用法');
          }
          if (!firstInvalid.tablets_per_dose) missing.push('数量');
          if (!isExternalOrInjection && !firstInvalid.days) missing.push('日数');

          if (missing.length > 0) {
            setAlertModal({ open: true, title: '入力エラー', message: `処方 ${firstInvalid.idx + 1}行目：${missing.join('・')}が未入力です。\n\n薬を追加した場合は、${isExternalOrInjection ? '用法・数量' : '用法・数量・日数'}をすべて入力してください。` });
            return;
          }
        }
      }
      
      // 書類の必須入力チェック（医師レビュー画面）
      for (let idx = 0; idx < docRows.length; idx++) {
        const r = docRows[idx];
        if (!r?.catalog_item_id) continue;
        const doc = docMaster.find(d => Number(d.id) === Number(r.catalog_item_id));
        const missing = getMissingRequiredDocumentFields(r);
        if (missing.length > 0) {
          const missingFieldNames = missing.map(key => DOC_FIELD_LABELS[key] || key).join('、');
          setAlertModal({
            open: true,
            title: '入力エラー',
            message: `書類 ${idx + 1} 件目（${doc?.name || ''}）に未入力の必須項目があります。\n\n未入力項目：${missingFieldNames}\n\n情報入力を開き、必要項目を入力してください。`
          });
          return;
        }
      }

      const payload = buildReviewPayload();
      const isUpdate = detail?.status === 'billing_confirmation_pending';
      const res = await axiosInstance.post(`/api/medichat_doctor/post_consult_reviews/${selectedSessionId}/submit`, payload);
      clearDoctorReviewDraftSnapshot(selectedSessionId);
      // モーダルを閉じた後にナビゲートするためにコールバックを設定
      setAlertModal({ 
        open: true, 
        title: '成功', 
        message: `${isUpdate ? '情報を更新しました。' : '登録しました。次の診察を選択してください。'}${res?.data?.conflict_detected ? '\n\n他画面の更新を上書きした可能性があります。' : ''}`,
        onCloseCallback: async () => {
          await loadList();
          setSelectedSessionId(null); 
          setDetail(null);
          navigate('/doctor/review');
        }
      });
    } catch (e) {
      setAlertModal({ open: true, title: 'エラー', message: e?.response?.data?.error || e.message || '送信に失敗しました' });
    } finally {
      setSubmitting(false);
      submitInFlightRef.current = false;
    }
  };

  // === 一時保存（診察終了せずに保存）===
  const saveDraft = async () => {
    if (!selectedSessionId) return;
    if (draftInFlightRef.current || submitting) return;
    draftInFlightRef.current = true;
    
    setSubmitting(true);
    try {
      const payload = buildReviewPayload();

      console.log('📝 一時保存 payload:', payload);
      
      const res = await axiosInstance.post(`/api/medichat_doctor/post_consult_reviews/${selectedSessionId}/save_draft`, payload);
      // AIタブ保存時は soap_* 同期済みの payload.record をスナップショットに残す
      setRec(payload.record || rec);
      saveDoctorReviewDraftSnapshot(
        selectedSessionId,
        {
          ...buildDraftSnapshot({
            serverPayloadRevision: res?.data?.server_payload_revision,
            serverPayloadHash: res?.data?.server_payload_hash,
          }),
          record: payload.record || rec,
        },
      );
      let reloadFailed = false;
      try {
        await Promise.all([
          loadDetail(selectedSessionId),
          loadList(),
        ]);
      } catch (reloadError) {
        reloadFailed = true;
        console.error('⚠️ 一時保存後の再取得に失敗:', reloadError);
      }
      
      const alreadyEnded = POST_FINISH_STATUSES.has(detail?.status);
      setAlertModal({ 
        open: true, 
        title: alreadyEnded ? '保存完了' : '一時保存完了', 
        message: `${alreadyEnded
          ? 'カルテ内容（手動カルテ・AI下書き）を保存しました。オーダー・会計は変更していません。'
          : '一時保存しました。\n診察終了ボタンを押すまで、会計は確定されません。'
        }${res?.data?.conflict_detected ? '\n\n他画面の更新を上書きした可能性があります。' : ''}${reloadFailed ? '\n\n最新状態の再取得に失敗しました。画面を再読み込みしてください。' : ''}`,
        iconType: 'success'
      });
    } catch (e) {
      console.error('❌ 一時保存エラー:', e);
      showAlert('エラー', e?.response?.data?.error || e.message || '一時保存に失敗しました', 'error');
    } finally {
      setSubmitting(false);
      draftInFlightRef.current = false;
    }
  };

  const undoCancel = async () => {
    if (!selectedSessionId || cancelling) return;

    showConfirm(
      '確認',
      'キャンセルを取り消して診察待ちに戻しますか？',
      async () => {
        setCancelling(true);
        try {
          await axiosInstance.post(`/api/medichat_doctor/post_consult_reviews/${selectedSessionId}/undo_cancel`);
          showAlert('成功', 'キャンセルを取り消しました', 'success', async () => {
            await Promise.all([
              loadList(),
              loadDetail(selectedSessionId),
            ]);
          });
        } catch (e) {
          showAlert('エラー', e?.response?.data?.error || 'キャンセル取り消しに失敗しました', 'error');
        } finally {
          setCancelling(false);
        }
      },
      'confirm'
    );
  };

  const sendEmergencyMeeting = () => {
    if (!selectedSessionId || sendingEmergencyMeeting || isCancelledDetail) return;

    const hasExisting = Boolean(detail?.emergency_meeting?.url);
    const confirmSuffix = '患者さんや運営に緊急用meetの発行が通知されます。この処理は取り消すことができません。通常時は通常の診察室での診察をお願いします。';
    const confirmMessage = hasExisting
      ? `既存の緊急用Google Meetを患者へ再送しますか？\n\n${confirmSuffix}`
      : `緊急用Google Meetを発行して患者へLINE送信しますか？\n\n${confirmSuffix}`;

    showConfirm(
      '確認',
      confirmMessage,
      async () => {
        setSendingEmergencyMeeting(true);
        try {
          const { data } = await axiosInstance.post(
            `/api/medichat_doctor/post_consult_reviews/${selectedSessionId}/send_emergency_meeting`,
          );
          await loadDetail(selectedSessionId);
          showAlert('成功', data?.message || '緊急用Meetを送信しました', 'success');
        } catch (e) {
          showAlert('エラー', e?.response?.data?.error || e.message || '緊急用Meetの送信に失敗しました', 'error');
        } finally {
          setSendingEmergencyMeeting(false);
        }
      },
      'confirm',
    );
  };

  const sendDoctorBotMessage = async (event) => {
    event?.preventDefault();
    const question = doctorBotInput.trim();
    if (!question || doctorBotLoading || !selectedSessionId) return;

    setDoctorBotMessages(prev => [...prev, { role: 'user', text: question }]);
    setDoctorBotInput('');
    setDoctorBotLoading(true);

    try {
      const { data } = await axiosInstance.post('/api/medichat_doctor/doctor_bot_chats', {
        session_id: selectedSessionId,
        message: question,
      }, {
        skipGlobalError: true,
        skipGlobalLoading: true,
      });
      setDoctorBotMessages(prev => [...prev, {
        role: 'bot',
        text: data?.answer || data?.error || '回答を取得できませんでした。もう一度お試しください。',
      }]);
    } catch (e) {
      setDoctorBotMessages(prev => [...prev, {
        role: 'bot',
        text: e?.response?.data?.error || '通信エラーが発生しました。時間をおいて再度お試しください。',
      }]);
    } finally {
      setDoctorBotLoading(false);
    }
  };

  const getSessionDateStr = (session) => {
    const dt = new Date(session.scheduled_at || session.started_at || session.created_at);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const todayDateStr = useMemo(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  }, []);

  const todayReviewSessions = useMemo(
    () => list.filter((s) => getSessionDateStr(s) === todayDateStr),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list, todayDateStr]
  );

  const allSessionsByDate = useMemo(() => {
    const groups = {};
    list.forEach((s) => {
      const dateStr = getSessionDateStr(s);
      if (!groups[dateStr]) {
        groups[dateStr] = { dateStr, dateObj: new Date(dateStr), isToday: dateStr === todayDateStr, sessions: [] };
      }
      groups[dateStr].sessions.push(s);
    });
    return Object.values(groups).sort((a, b) => b.dateObj - a.dateObj);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, todayDateStr]);

  const getReviewDateGroupLabel = (dateGroup) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    if (dateGroup.isToday) return '今日の診察';
    if (dateGroup.dateObj.getTime() === tomorrow.getTime()) return '明日の診察';
    const m = dateGroup.dateObj.getMonth() + 1;
    const d = dateGroup.dateObj.getDate();
    return `${m}/${d}の診察`;
  };

  /* セッションカード共通レンダラー（新デザイン） */
  const renderSessionCard = (s) => {
    const consultationModeLabel = s.consultation_mode === 'first_visit' ? '初診' : s.consultation_mode === 'follow_up' ? '再診' : '-';
    const coverageKindLabel = s.coverage_kind === 'insurance' ? '保険' : s.coverage_kind === 'self_pay' ? '自費' : '-';
    const isCancelled = s.status === 'cancelled';
    const statusLabel = {
      waiting: '診察待ち', in_progress: '診察中', doctor_finished: '診察終了',
      record_review_pending: '確認待ち', billing_confirmation_pending: '会計中', cancelled: 'キャンセル',
    }[s.status] || s.status;
    const questionnaireBadge = getQuestionnaireStateBadge(s);
    const timeStr = new Date(s.scheduled_at || s.started_at || s.created_at).toLocaleTimeString('ja-JP', {hour: '2-digit', minute: '2-digit'});

    const statusAccent = {
      waiting: 'accent--waiting', in_progress: 'accent--active', doctor_finished: 'accent--done',
      record_review_pending: 'accent--pending', billing_confirmation_pending: 'accent--billing', cancelled: 'accent--cancelled',
    }[s.status] || '';

    const statusTagClass = {
      waiting: 'mc-mode-tag--waiting', in_progress: 'mc-mode-tag--in-progress',
      record_review_pending: 'mc-mode-tag--pending', billing_confirmation_pending: 'mc-mode-tag--billing',
      cancelled: 'mc-mode-tag--cancelled',
    }[s.status] || 'mc-mode-tag--cancelled';
    const patientName = s.patient?.name || s.patient_display_name || s.patient_name || '-';
    const patientId = s.patient?.id || s.patient_id || '-';

    return (
      <div
        key={s.session_id}
        className={`session-card ${statusAccent} ${isCancelled ? 'is-cancelled' : ''}`}
        onClick={() => navigate(`/doctor/review/${s.session_id}`)}
      >
        <div className="session-card__top">
          <span className="session-card__time">{timeStr}</span>
          <span className="session-card__name">{patientName}</span>
          <span className={`mc-mode-tag ${statusTagClass} session-card__status`}>{statusLabel}</span>
        </div>
        <div className="session-card__tags">
          <span className={`mc-mode-tag ${s.consultation_mode === 'first_visit' ? 'mc-mode-tag--first-visit' : 'mc-mode-tag--follow-up'}`}>{consultationModeLabel}</span>
          <span className={`mc-mode-tag ${s.coverage_kind === 'insurance' ? 'mc-mode-tag--insurance' : 'mc-mode-tag--self-pay'}`}>{coverageKindLabel}</span>
          {s.primary_disease && <span className="session-card__disease">{s.primary_disease.name}</span>}
          {questionnaireBadge && (
            <span className="mc-mode-tag" style={{...questionnaireBadge.style, padding: '0 6px', borderRadius: '4px', fontSize: '10px', fontWeight: '700', border: 'none'}}>
              {questionnaireBadge.label}
            </span>
          )}
        </div>
        <div className="session-card__footer">
          <span className="session-card__ids">予約{s.session_id} · ID{patientId}</span>
          <span className="session-card__arrow">›</span>
        </div>
      </div>
    );
  };

  const getQuestionnaireStateBadge = (session) => {
    if (!session || session.status !== 'waiting' || session.flow_version !== 'v2_reserve_first') return null;

    if (session.pre_questionnaire_completion_reason === 'timeout_skip') {
      return { label: '問診 timeout完了', style: { background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' } };
    }
    if (session.pre_questionnaire_completed) {
      return { label: '問診完了', style: { background: '#dcfce7', color: '#166534', border: '1px solid #86efac' } };
    }
    return { label: '問診未完了', style: { background: '#e0f2fe', color: '#0c4a6e', border: '1px solid #7dd3fc' } };
  };

  /* レンダリング */
  if (!selectedSessionId) {
    const doctorName = doctorProfile ? `${doctorProfile.last_name || ''} ${doctorProfile.first_name || ''}`.trim() : '';
    
    return (
      <div className="doctor-page-shell doctor-page-shell--wide">
        <div className="medichat_profile-container">
          {/* ヘッダー: タイトル左・医師名右 */}
          <div className="review-list-header">
            <span className="review-list-header__title">カルテ・診察開始</span>
            {doctorName && (
              <span className="doctor-status-pill doctor-status-pill--active">{doctorName}医師</span>
            )}
          </div>
          {/* 診察室へ入るCTA */}
          <a
            href={doctorProfile?.video_carte_url || 'https://video-carte-web-yokz.vercel.app/login?openExternalBrowser=1'}
            target="_blank"
            rel="noopener noreferrer"
            className="review-room-cta"
          >
            <i className="fas fa-video" style={{marginRight: '8px'}}></i>
            診察室へ入る
          </a>

          {/* 本日 / 全て タブ */}
          <div className="review-list-tabs">
            <button type="button" className={`review-list-tab ${reviewListTab === 'today' ? 'is-active' : ''}`} onClick={() => setReviewListTab('today')}>
              本日の診察
            </button>
            <button type="button" className={`review-list-tab ${reviewListTab === 'all' ? 'is-active' : ''}`} onClick={() => setReviewListTab('all')}>
              全ての診察
            </button>
          </div>

        <div className="medichat-content">
          {loadingList ? <div className="medichat-loading">読み込み中...</div>
          : reviewListTab === 'today' ? (
            todayReviewSessions.length === 0
              ? <div className="medichat-empty" style={{padding: '32px 16px', textAlign: 'center', color: '#9ca3af'}}>本日の診察はありません</div>
              : (
                <div className="medichat-session-list medichat-session-list--flat">
                  {todayReviewSessions.map(s => renderSessionCard(s))}
                </div>
              )
          ) : allSessionsByDate.length === 0 ? <div className="medichat-empty">対象はありません</div>
          : (
            <div className="medichat-date-group-list">
              {allSessionsByDate.map(dateGroup => {
                const isOpen = openDates.has(dateGroup.dateStr);
                const isToday = dateGroup.isToday;
                const dateLabel = getReviewDateGroupLabel(dateGroup);
                
                const toggleDate = () => {
                  const newOpenDates = new Set(openDates);
                  if (isOpen) {
                    newOpenDates.delete(dateGroup.dateStr);
                  } else {
                    newOpenDates.add(dateGroup.dateStr);
                  }
                  setOpenDates(newOpenDates);
                };
                
                return (
                  <div key={dateGroup.dateStr} className={`medichat-date-group ${isToday ? 'is-today' : ''}`}>
                    <div className="medichat-date-group-header" onClick={toggleDate}>
                      <div className="medichat-date-group-title">
                        {isToday && <span className="date-group-today-dot" />}
                        <span>{dateLabel}</span>
                        <span className="medichat-date-group-count">{dateGroup.sessions.length}件</span>
                      </div>
                      <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`} style={{fontSize: '12px'}}></i>
                    </div>
                    
                    {isOpen && (
                      <div className="medichat-session-list">
                        {dateGroup.sessions.map(s => renderSessionCard(s))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </div>
      </div>
    );
  }

  const coverageLabel = coverageKindLive === 'self_pay' ? '自費診療' : '保険診療';
  // Hooksルールに抵触しないよう、通常の式で判定
  const canSubmitReview = (() => {
    if (submitting) return false;
    // 主疾患は常に必須
    if (!primaryDiseaseId) return false;
    if (coverageKindLive !== 'self_pay') {
      const invalidOrders = orderRows
        .map((row, idx) => ({ ...row, idx }))
        .filter(row => row.catalog_item_id)
        .filter(row => {
          // 外用薬・注射薬の場合は日数不要
          const isExternalOrInjection = EXTERNAL_INJECTION_GROUPS.includes(row.usage_group);
          if (isExternalOrInjection) {
            return !row.dosage_pattern_id || !row.tablets_per_dose;
          }
          return !row.dosage_pattern_id || !row.tablets_per_dose || !row.days;
        });
      if (invalidOrders.length > 0) return false;
      for (const r of docRows) {
        if (getMissingRequiredDocumentFields(r).length > 0) return false;
      }
    }
    return true;
  })();
  const jpGender = (g) => {
    const s = (g ?? '').toString();
    if (['male','0','男','男性','M','m'].includes(s))   return '男性';
    if (['female','1','女','女性','F','f'].includes(s)) return '女性';
    return 'その他';
  };
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '-';
  const modeLabel = (detail?.consultation_mode === 'first_visit') ? '初診' :
                    (detail?.consultation_mode === 'follow_up')   ? '再診' : '-';

  const primaryDiseaseName = detail?.primary_disease?.name || detail?.primary_disease?.disease_name || '-';
  const detailQuestionnaireBadge = getQuestionnaireStateBadge(detail);
  const isCancelledDetail = detail?.status === 'cancelled';
  const isPostFinishDetail = POST_FINISH_STATUSES.has(detail?.status);
  // 診察終了後はオーダー・主疾患を編集させない（カルテ本文のみ）
  const isOrdersLocked = isCancelledDetail || isPostFinishDetail;
  const emergencyMeetingButtonLabel = sendingEmergencyMeeting
    ? '送信中...'
    : (detail?.emergency_meeting?.url ? '緊急用Meetを再送' : '緊急用Meetを発行');

  const roomIsKaraken = !isCancelledDetail && !!detail?.consultation_room?.is_karaken_migrated;
  const roomHasKarakenZoom = roomIsKaraken && !!detail?.consultation_room?.karaken_zoom_url;
  const roomHasKarakenAdmin = roomIsKaraken && !!detail?.consultation_room?.karaken_admin_url;
  const roomHasDoctorRoom = !isCancelledDetail && !roomIsKaraken && !!detail?.consultation_room?.doctor_room_url;
  const hasAnyRoomChip = roomHasKarakenZoom || roomHasKarakenAdmin || roomHasDoctorRoom;

  return (
    <div className={`dr-review-shell${isCancelledDetail ? ' is-cancelled-review' : ''}`}>
      {/* ===== 固定ヘッダー ===== */}
      <div className="dr-review-fixed-header dr-review-fixed-header--compact">
        <div className="dr-review-header-main">
          <h1 className="dr-review-header-title">{detail?.patient?.name || 'カルテ'}</h1>
          <span className="dr-review-header-meta">予約{detail?.session_id} · ID{detail?.patient?.id}</span>
        </div>
        <div className="dr-review-header-tags">
          <span className={`mc-mode-tag ${coverageKindLive === 'insurance' ? 'mc-mode-tag--insurance' : 'mc-mode-tag--self-pay'}`}>
            {coverageLabel}
          </span>
          <span className={`mc-mode-tag ${detail?.consultation_mode === 'first_visit' ? 'mc-mode-tag--first-visit' : 'mc-mode-tag--follow-up'}`}>
            {modeLabel}
          </span>
          {primaryDiseaseName && primaryDiseaseName !== '-' && (
            <span className="dr-review-header-disease">{primaryDiseaseName}</span>
          )}
          {detailQuestionnaireBadge && (
            <span className="mc-mode-tag" style={detailQuestionnaireBadge.style}>
              {detailQuestionnaireBadge.label}
            </span>
          )}
          {isCancelledDetail && (
            <span className="mc-mode-tag mc-mode-tag--cancelled">キャンセル済み</span>
          )}
        </div>

        {/* 診察室URL関連チップ行（Zoom/診察室/旧管理） */}
        {hasAnyRoomChip && (
          <div className="dr-quick-actions">
            {roomHasKarakenZoom && (
              <a
                href={detail.consultation_room.karaken_zoom_url}
                target="_blank"
                rel="noopener noreferrer"
                className="dr-chip dr-chip--warning"
              >
                <i className="fas fa-video" aria-hidden="true"></i>
                <span>Zoom診察室</span>
              </a>
            )}
            {roomHasKarakenAdmin && (
              <a
                href={detail.consultation_room.karaken_admin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="dr-chip dr-chip--muted"
              >
                <i className="fas fa-folder-open" aria-hidden="true"></i>
                <span>旧管理</span>
              </a>
            )}
            {roomHasDoctorRoom && (
              <a
                href={detail.consultation_room.doctor_room_url}
                target="_blank"
                rel="noopener noreferrer"
                className="dr-chip dr-chip--primary"
              >
                <i className="fas fa-user-doctor" aria-hidden="true"></i>
                <span>診察室</span>
              </a>
            )}
          </div>
        )}

        {/* 緊急用Meet（発行ボタン付き、診察室URLの有無に依存せず常時表示） */}
        {!isCancelledDetail && (
          <div className="dr-quick-actions dr-quick-actions--emergency">
            <span className="dr-quick-actions__emergency-label">🚨 緊急用Meet</span>
            {detail?.emergency_meeting?.url && (
              <a
                href={detail.emergency_meeting.url}
                target="_blank"
                rel="noopener noreferrer"
                className="dr-chip dr-chip--danger"
              >
                <span>緊急用Meetはこちら</span>
              </a>
            )}
            <button
              type="button"
              onClick={sendEmergencyMeeting}
              disabled={sendingEmergencyMeeting}
              className="dr-chip dr-chip--emergency-action"
            >
              {emergencyMeetingButtonLabel}
            </button>
          </div>
        )}

      </div>{/* /dr-review-fixed-header */}

      {/* ===== スクロール可能なボディ ===== */}
      <div className="dr-review-body" ref={bodyRef}>
        {loadingDetail ? (
          <div className="medichat-loading">読込中...</div>
        ) : detail ? (
          <>
            {isCancelledDetail && (
              <div style={{background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: '12px', padding: '12px 14px', margin: '0 0 14px'}}>
                <div style={{fontWeight: 700, fontSize: '14px', marginBottom: '6px'}}>❌ キャンセル済み</div>
                <div style={{fontSize: '13px', lineHeight: 1.7}}>
                  <div><strong>キャンセル日時:</strong> {detail.cancelled_at ? new Date(detail.cancelled_at).toLocaleString('ja-JP') : '-'}</div>
                  <div><strong>理由:</strong> {detail.cancellation_reason || '（理由なし）'}</div>
                  {Number(detail.cancellation_fee || 0) > 0 && (
                    <div><strong>キャンセル料:</strong> ¥{Number(detail.cancellation_fee).toLocaleString()}</div>
                  )}
                </div>
              </div>
            )}

            {/* ==============================
                基本情報 タブ
            ============================== */}
            {activeMainTab === 'info' && (
              <>
                {/* サブタブバー */}
                <div className="dr-sub-tabbar">
                  <button
                    type="button"
                    onClick={() => setActiveInfoSubTab('questionnaire')}
                    className={`dr-sub-tab${activeInfoSubTab === 'questionnaire' ? ' is-active' : ''}`}
                  >問診</button>
                  <button
                    type="button"
                    onClick={() => setActiveInfoSubTab('profile')}
                    className={`dr-sub-tab${activeInfoSubTab === 'profile' ? ' is-active' : ''}`}
                  >患者情報</button>
                  <button
                    type="button"
                    onClick={() => setActiveInfoSubTab('files')}
                    className={`dr-sub-tab${activeInfoSubTab === 'files' ? ' is-active' : ''}`}
                  >ファイル</button>
                  <button
                    type="button"
                    onClick={() => setActiveInfoSubTab('history')}
                    className={`dr-sub-tab${activeInfoSubTab === 'history' ? ' is-active' : ''}`}
                  >過去カルテ</button>
                </div>

                <div className="dr-sub-content">

                  {/* ---- 問診サブタブ ---- */}
                  {activeInfoSubTab === 'questionnaire' && (
                    <>
                      {/* 過去受診歴警告 */}
                      {detail?.past_visit_warning && (
                        <div style={{
                          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                          border: '2px solid #f59e0b',
                          borderRadius: '10px',
                          padding: '12px 14px',
                          marginBottom: '12px',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '10px',
                        }}>
                          <span style={{ fontSize: '20px' }}>⚠️</span>
                          <div style={{ flex: 1, fontSize: '13px', color: '#92400e' }}>
                            <div style={{ fontWeight: 700, marginBottom: '2px' }}>同じ診療科での過去受診歴があります</div>
                            <div>{detail.past_visit_warning.specialty_name} / {detail.past_visit_warning.disease_name}（{detail.past_visit_warning.visit_date}）</div>
                          </div>
                        </div>
                      )}

                      {/* 問診回答セクション */}
                      <div className="medichat-qa-section">
                        <div className="medichat-qa-content">
                          {(!detail?.questionnaire_answers_by_session || detail.questionnaire_answers_by_session.length === 0) ? (
                            <div className="medichat-qa-empty">問診回答がありません</div>
                          ) : (
                            <div className="medichat-qa-list" style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                              {(() => {
                                const sessions = detail.questionnaire_answers_by_session;
                                const currentSession = sessions.find(s => s.is_current);
                                const otherSessions = sessions.filter(s => !s.is_current);
                                const orderedSessions = [currentSession, ...otherSessions].filter(Boolean);
                                
                                return orderedSessions.map((session) => {
                                  const isExpanded = expandedQaSessions.has(session.session_id);
                                  const toggleExpand = () => {
                                    setExpandedQaSessions(prev => {
                                      const next = new Set(prev);
                                      if (next.has(session.session_id)) {
                                        next.delete(session.session_id);
                                      } else {
                                        next.add(session.session_id);
                                      }
                                      return next;
                                    });
                                  };

                                  return (
                                    <div key={session.session_id} style={{
                                      border: session.is_current ? '1px solid #3b82f6' : '1px solid #e2e8f0',
                                      borderRadius: '8px',
                                      overflow: 'hidden'
                                    }}>
                                      <div
                                        onClick={toggleExpand}
                                        style={{
                                          background: session.is_current ? '#3b82f6' : '#f8fafc',
                                          color: session.is_current ? '#fff' : '#1e293b',
                                          padding: '10px 12px',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          fontWeight: 600
                                        }}
                                      >
                                        <div style={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px'}}>
                                          <span style={{fontSize: '12px'}}>{session.display_date}</span>
                                          <span style={{fontSize: '12px', opacity: 0.9}}>{session.disease_name}</span>
                                          <span style={{
                                            padding: '1px 6px',
                                            borderRadius: '10px',
                                            fontSize: '10px',
                                            fontWeight: 600,
                                            background: session.is_current
                                              ? 'rgba(255,255,255,0.25)'
                                              : (session.consultation_mode === 'first_visit' ? '#fef3c7' : '#dbeafe'),
                                            color: session.is_current
                                              ? '#fff'
                                              : (session.consultation_mode === 'first_visit' ? '#92400e' : '#1e40af')
                                          }}>
                                            {session.consultation_mode_label}
                                          </span>
                                          {session.is_current && (
                                            <span style={{
                                              padding: '1px 6px',
                                              borderRadius: '10px',
                                              fontSize: '10px',
                                              fontWeight: 600,
                                              background: 'rgba(255,255,255,0.3)'
                                            }}>今回</span>
                                          )}
                                        </div>
                                        <span style={{
                                          fontSize: '12px',
                                          fontWeight: 'bold',
                                          color: session.is_current ? '#fff' : '#64748b',
                                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                          transition: 'transform 0.2s ease',
                                          display: 'inline-block'
                                        }}>▼</span>
                                      </div>
                                      {isExpanded && (
                                        <div style={{padding: '10px', background: '#fff'}}>
                                          {(!session.answers || session.answers.length === 0) ? (
                                            <div style={{textAlign: 'left', padding: '8px', color: '#94a3b8', fontSize: '12px'}}>
                                              この診察の問診回答はありません
                                            </div>
                                          ) : (
                                            session.answers.map((qa, i) => {
                                              const answerVal = qa.answer;
                                              const files = extractQuestionnaireAnswerFiles(answerVal);
                                              const hasFiles = files.length > 0;
                                              return (
                                                <div key={i} style={{
                                                  marginBottom: '6px',
                                                  padding: '6px 8px',
                                                  background: '#f8fafc',
                                                  borderRadius: '4px'
                                                }}>
                                                  <div style={{margin: '0 0 2px', fontWeight: 600, color: '#334155', fontSize: '12px', textAlign: 'left'}}>
                                                    <span style={{color: '#3b82f6'}}>Q.</span> {qa.question_text}
                                                  </div>
                                                  <div style={{margin: 0, color: '#64748b', fontSize: '12px', textAlign: 'left', paddingLeft: '12px'}}>
                                                    <span style={{color: '#ef4444', fontWeight: 600}}>A.</span>{' '}
                                                    {hasFiles ? (
                                                      <button
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setViewerFiles(files);
                                                          setViewerTitle(qa.question_text || 'アップロードファイル');
                                                          setFileViewerOpen(true);
                                                        }}
                                                        style={{
                                                          padding: 0,
                                                          background: 'transparent',
                                                          color: '#2563eb',
                                                          border: 'none',
                                                          cursor: 'pointer',
                                                          fontSize: '12px',
                                                          fontWeight: 700,
                                                          textDecoration: 'underline'
                                                        }}
                                                      >画像を開く</button>
                                                    ) : (
                                                      Array.isArray(qa.answer) ? qa.answer.join('、') : String(qa.answer ?? '-')
                                                    )}
                                                  </div>
                                                </div>
                                              );
                                            })
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          )}

                          {/* からけん時代の問診（トグル形式） */}
                          {detail?.karaken_questionnaires && detail.karaken_questionnaires.length > 0 && (
                            <div style={{ marginTop: '12px' }}>
                              <button
                                type="button"
                                onClick={() => setKarakenQaOpen(v => !v)}
                                style={{
                                  width: '100%',
                                  padding: '10px 14px',
                                  background: karakenQaOpen ? '#faf5ff' : '#f3e8ff',
                                  border: '1px dashed #c084fc',
                                  borderRadius: '8px',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  cursor: 'pointer',
                                }}
                              >
                                <span style={{ fontSize: '13px', color: '#7c3aed', fontWeight: '600' }}>📚 からけん時代の問診</span>
                                <span style={{ color: '#7c3aed', transform: karakenQaOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease', display: 'inline-block' }}>▼</span>
                              </button>
                              {karakenQaOpen && (
                                <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                  {detail.karaken_questionnaires.map((kq, idx) => (
                                    <div key={`karaken-q-${kq.response_id || idx}`} style={{
                                      border: '1px solid #e9d5ff',
                                      borderRadius: '8px',
                                      overflow: 'hidden'
                                    }}>
                                      <div style={{
                                        background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
                                        color: '#581c87',
                                        padding: '10px 12px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        fontWeight: 600
                                      }}>
                                        <div style={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px'}}>
                                          <span style={{fontSize: '12px'}}>
                                            {kq.created_at ? new Date(kq.created_at).toLocaleDateString('ja-JP') : '-'}
                                          </span>
                                          <span style={{fontSize: '12px', opacity: 0.9}}>{kq.questionnaire_title || '問診'}</span>
                                          <span style={{
                                            padding: '1px 6px',
                                            borderRadius: '10px',
                                            fontSize: '10px',
                                            fontWeight: 600,
                                            background: '#c4b5fd',
                                            color: '#4c1d95'
                                          }}>からけん</span>
                                        </div>
                                      </div>
                                      <div style={{padding: '10px', background: '#fefbff'}}>
                                        {(!kq.qa_pairs || kq.qa_pairs.length === 0) ? (
                                          <div style={{textAlign: 'left', padding: '8px', color: '#94a3b8', fontSize: '12px'}}>
                                            問診回答がありません
                                          </div>
                                        ) : (
                                          kq.qa_pairs.map((qa, i) => (
                                            <div key={i} style={{
                                              marginBottom: '6px',
                                              padding: '6px 8px',
                                              background: '#f5f3ff',
                                              borderRadius: '4px'
                                            }}>
                                              <div style={{margin: '0 0 2px', fontWeight: 600, color: '#581c87', fontSize: '12px', textAlign: 'left'}}>
                                                <span style={{color: '#7c3aed'}}>Q.</span> {qa.question}
                                              </div>
                                              <div style={{margin: 0, color: '#7c3aed', fontSize: '12px', textAlign: 'left', paddingLeft: '12px'}}>
                                                <span style={{color: '#a855f7', fontWeight: 600}}>A.</span> {qa.answer || '（未回答）'}
                                              </div>
                                            </div>
                                          ))
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* ---- 患者情報サブタブ ---- */}
                  {activeInfoSubTab === 'profile' && (
                    <>
                      <ClinicalInsightPanelV1
                        clinicalInsights={detail?.clinical_insights}
                      />

                      <div className="medichat-patient-section">
                        <div className="dr-section-title">👤 患者プロフィール</div>
                        <div className="medichat-patient-grid" style={{marginTop: '10px'}}>
                          <div className="medichat-patient-item">氏名: {detail?.patient?.name || '-'}</div>
                          <div className="medichat-patient-item">性別: {jpGender(detail?.patient?.gender)}</div>
                          <div className="medichat-patient-item">生年月日: {fmtDate(detail?.patient?.birth_date)}</div>
                          <div className="medichat-patient-item">年齢: {detail?.patient?.age ?? '-'}</div>
                          <div className="medichat-patient-item">居住都道府県: {detail?.patient?.prefecture || '-'}</div>
                          <div className="medichat-patient-item">身長: {detail?.patient?.height ?? '-'} cm</div>
                          <div className="medichat-patient-item">体重: {detail?.patient?.weight ?? '-'} kg</div>
                          <div className="medichat-patient-item">BMI: {detail?.patient?.bmi ?? '-'}</div>
                          <div className="medichat-patient-item">アレルギー: {detail?.patient?.allergies || '-'}</div>
                          <div className="medichat-patient-item">アレルギー詳細: {detail?.patient?.allergy_details || '-'}</div>
                          <div className="medichat-patient-item">既往歴: {detail?.patient?.medical_history || '-'}</div>
                          <div className="medichat-patient-item">服薬内容: {detail?.patient?.current_medications || '-'}</div>
                          <div className="medichat-patient-item">妊娠状況: {detail?.patient?.pregnancy_status || '-'}</div>
                          <div className="medichat-patient-item">電話番号: {detail?.patient?.phone_number || '-'}</div>
                          <div className="medichat-patient-item">メール: {detail?.patient?.email_user || '-'}</div>
                        </div>
                      </div>

                      {/* 傷病名セクション */}
                      <div className="medichat-patient-section">
                        <div className="dr-section-title">🩺 傷病名</div>
                  <div style={{marginTop: '10px'}}>
                    {/* ORCA同期ボタン */}
                    <div style={{marginBottom: '12px', display: 'flex', justifyContent: 'flex-end'}}>
                      <button
                        onClick={syncDiagnosesFromOrca}
                        disabled={syncingOrca}
                        style={{
                          padding: '8px 14px',
                          background: syncingOrca ? '#9ca3af' : 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: syncingOrca ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          boxShadow: '0 2px 4px rgba(249, 115, 22, 0.3)'
                        }}
                      >
                        {syncingOrca ? (
                          <>
                            <i className="fas fa-spinner fa-spin"></i>
                            同期中...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-rotate"></i>
                            ORCAから同期
                          </>
                        )}
                      </button>
                    </div>
                    
                    {/* 傷病名一覧 */}
                    {patientDiagnoses.length === 0 ? (
                      <div style={{textAlign: 'center', color: '#6b7280', padding: '20px', background: '#f9fafb', borderRadius: '8px'}}>
                        傷病名が登録されていません
                      </div>
                    ) : (
                      <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                        {patientDiagnoses.map((diag) => (
                          <div 
                            key={diag.id}
                            style={{
                              padding: '12px 16px',
                              background: '#fff',
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                            }}
                          >
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap'}}>
                              <div style={{flex: 1, minWidth: 0}}>
                                <span style={{fontWeight: '600', color: '#1e293b', fontSize: '14px'}}>{diag.disease_name}</span>
                              </div>
                              <div style={{display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, flexWrap: 'wrap'}}>
                                {diag.is_main_disease && (
                                  <span style={{fontSize: '11px', background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: '4px', fontWeight: '600'}}>主病</span>
                                )}
                                {diag.is_suspected && (
                                  <span style={{fontSize: '11px', background: '#e0e7ff', color: '#4338ca', padding: '2px 8px', borderRadius: '4px', fontWeight: '600'}}>疑い</span>
                                )}
                                {diag.is_acute && (
                                  <span style={{fontSize: '11px', background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '4px', fontWeight: '600'}}>急性</span>
                                )}
                              </div>
                            </div>
                            <div style={{marginTop: '8px', fontSize: '12px', color: '#64748b', display: 'flex', gap: '16px', flexWrap: 'wrap'}}>
                              <span>開始日: {diag.onset_date || '-'}</span>
                              {diag.end_date && <span>終了日: {diag.end_date}</span>}
                              {diag.outcome_label && <span>転帰: {diag.outcome_label}</span>}
                            </div>
                            {diag.orca_synced_at && (
                              <div style={{marginTop: '6px', fontSize: '11px', color: '#94a3b8'}}>
                                <i className="fas fa-check-circle" style={{marginRight: '4px', color: '#22c55e'}}></i>
                                ORCA同期: {diag.orca_synced_at}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    </div>
              </div>
                    </>
                  )}

                  {/* ---- ファイルサブタブ ---- */}
                  {activeInfoSubTab === 'files' && (
                    <div className="dr-sub-content">
              {/* ファイルセクション */}
              <div className="medichat-patient-section">
                  <div style={{marginTop: '0px'}}>
                    {/* 患者アップロードファイル一覧 */}
                    <div style={{marginBottom: '20px'}}>
                      <div style={{fontWeight: '600', fontSize: '14px', color: '#1e293b', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px'}}>
                        📤 患者アップロードファイル
                      </div>
                      {(!detail?.patient_documents || detail.patient_documents.length === 0) ? (
                        <div style={{textAlign: 'center', color: '#6b7280', padding: '16px', background: '#f9fafb', borderRadius: '8px'}}>
                          まだ登録がありません
                        </div>
                      ) : (
                        <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                          {detail.patient_documents.map((doc) => (
                            <div
                              key={doc.id}
                              onClick={() => {
                                setViewerFiles([doc.file_url]);
                                setViewerTitle(doc.doc_type_label);
                                setFileViewerOpen(true);
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '12px',
                                background: '#f9fafb',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                            >
                              <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                                <span style={{fontSize: '20px'}}>
                                  {doc.doc_type === 'medical_report' ? '🩺' : doc.doc_type === 'medicine_notebook' ? '💊' : '📎'}
                                </span>
                                <div>
                                  <div style={{fontWeight: '500', color: '#1e293b', fontSize: '14px'}}>
                                    {doc.doc_type_label}
                                  </div>
                                </div>
                              </div>
                              <div style={{textAlign: 'right'}}>
                                <div style={{fontSize: '12px', color: '#64748b'}}>
                                  {new Date(doc.created_at).toLocaleDateString('ja-JP', {year: 'numeric', month: '2-digit', day: '2-digit'})}
                                </div>
                                <div style={{fontSize: '11px', color: '#3b82f6', marginTop: '2px'}}>
                                  タップで表示 →
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* からけん時代のファイル（検査結果） */}
                    {detail?.karaken_medical_results && detail.karaken_medical_results.length > 0 && (
                      <div style={{marginBottom: '20px'}}>
                        <div style={{fontWeight: '600', fontSize: '14px', color: '#7c3aed', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px'}}>
                          📁 からけん時代のファイル
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '10px',
                            fontSize: '10px',
                            fontWeight: 600,
                            background: '#c4b5fd',
                            color: '#4c1d95'
                          }}>
                            旧システム
                          </span>
                        </div>
                        <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                          {detail.karaken_medical_results.map((result) => (
                            <a
                              key={result.id}
                              href={result.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '12px',
                                background: '#fef3c7',
                                borderRadius: '8px',
                                textDecoration: 'none',
                                color: 'inherit',
                                border: '1px solid #f59e0b',
                                gap: '12px'
                              }}
                            >
                              <div style={{fontSize: '24px'}}>
                                {result.flag === 2 ? '🩸' : result.flag === 3 ? '📋' : '📄'}
                              </div>
                              <div style={{flex: 1}}>
                                <div style={{fontWeight: '600', fontSize: '14px', color: '#92400e'}}>
                                  {result.title || result.flag_label || 'ファイル'}
                                </div>
                                <div style={{fontSize: '11px', color: '#b45309', marginTop: '2px'}}>
                                  {result.flag_label}
                                  {result.created_at && ` • ${new Date(result.created_at).toLocaleDateString('ja-JP')}`}
                                </div>
                              </div>
                              <div style={{color: '#f59e0b', fontSize: '12px'}}>
                                開く →
                              </div>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* 診察履歴と発行書類一覧（今後実装予定） */}
                    <div>
                      <div style={{fontWeight: '600', fontSize: '14px', color: '#1e293b', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px'}}>
                        📋 発行書類
                      </div>
                      <div style={{textAlign: 'center', color: '#94a3b8', padding: '16px', background: '#f9fafb', borderRadius: '8px', fontSize: '13px'}}>
                        管理画面から確認してください
                      </div>
                    </div>
                    </div>
              </div>
                    </div>
                  )}

                  {/* ---- 過去カルテサブタブ ---- */}
                  {activeInfoSubTab === 'history' && (
                    <div className="dr-sub-content">
              {/* 過去カルテセクション */}
              <div className="medichat-patient-section">
                <div className="dr-section-title">📋 過去のカルテ</div>
                  <div style={{marginTop: '10px'}}>
                    {(!detail?.past_records || detail.past_records.length === 0) ? (
                      <div style={{textAlign: 'center', color: '#6b7280', padding: '20px', background: '#f9fafb', borderRadius: '8px'}}>
                        過去のカルテ記録がありません
                      </div>
                    ) : (
                      <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                        {detail.past_records.map((pastRecord, idx) => (
                          <div 
                            key={pastRecord.session_id || idx}
                            style={{
                              border: '1px solid #e2e8f0',
                              borderRadius: '12px',
                              overflow: 'hidden',
                              background: '#fff'
                            }}
                          >
                            {/* カルテヘッダー */}
                            <div style={{
                              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                              padding: '12px 16px',
                              borderBottom: '1px solid #e2e8f0',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: '8px'
                            }}>
                              <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
                                <span style={{fontSize: '16px'}}>🗓️</span>
                                <span style={{fontWeight: '600', color: '#1e293b', fontSize: '14px'}}>
                                  {pastRecord.visit_date}
                                </span>
                                <span style={{
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  background: pastRecord.consultation_mode === 'first_visit' ? '#fef3c7' : '#dbeafe',
                                  color: pastRecord.consultation_mode === 'first_visit' ? '#92400e' : '#1e40af'
                                }}>
                                  {pastRecord.consultation_mode_label}
                                </span>
                                <span style={{
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  background: pastRecord.coverage_kind?.includes('insurance') ? '#d1fae5' : '#fce7f3',
                                  color: pastRecord.coverage_kind?.includes('insurance') ? '#065f46' : '#9d174d'
                                }}>
                                  {pastRecord.coverage_kind_label}
                                </span>
                              </div>
                              <div style={{fontSize: '12px', color: '#64748b'}}>
                                担当: {pastRecord.doctor_name}
                              </div>
                            </div>

                            {/* 疾患名 */}
                            <div style={{
                              padding: '8px 16px',
                              borderBottom: '1px solid #f1f5f9',
                              background: '#fafbfc'
                            }}>
                              <span style={{fontSize: '13px', color: '#64748b'}}>主疾患: </span>
                              <span style={{fontSize: '13px', fontWeight: '600', color: '#334155'}}>{pastRecord.disease_name}</span>
                            </div>

                            {/* SOAP内容 */}
                            <div style={{padding: '16px'}}>
                              {pastRecord.has_record === false && (
                                <div style={{
                                  background: '#fef3c7',
                                  border: '1px solid #fcd34d',
                                  borderRadius: '8px',
                                  padding: '16px',
                                  marginBottom: '12px'
                                }}>
                                  <p style={{color: '#92400e', fontWeight: '600', margin: '0 0 8px'}}>⚠️ カルテ未入力</p>
                                  <p style={{color: '#78716c', fontSize: '13px', margin: 0}}>このセッションにはまだカルテが入力されていません。</p>
                                </div>
                              )}
                              {pastRecord.soap_subjective && (
                                <div style={{marginBottom: '12px'}}>
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    marginBottom: '6px'
                                  }}>
                                    <span style={{
                                      background: '#3b82f6',
                                      color: '#fff',
                                      width: '20px',
                                      height: '20px',
                                      borderRadius: '4px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '11px',
                                      fontWeight: '700'
                                    }}>S</span>
                                    <span style={{fontSize: '12px', fontWeight: '600', color: '#475569'}}>主観的症状</span>
                                  </div>
                                  <div style={{
                                    background: '#f8fafc',
                                    padding: '10px 12px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    lineHeight: '1.6',
                                    color: '#334155',
                                    whiteSpace: 'pre-wrap'
                                  }}>
                                    {pastRecord.soap_subjective}
                                  </div>
                                </div>
                              )}

                              {pastRecord.soap_objective && (
                                <div style={{marginBottom: '12px'}}>
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    marginBottom: '6px'
                                  }}>
                                    <span style={{
                                      background: '#10b981',
                                      color: '#fff',
                                      width: '20px',
                                      height: '20px',
                                      borderRadius: '4px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '11px',
                                      fontWeight: '700'
                                    }}>O</span>
                                    <span style={{fontSize: '12px', fontWeight: '600', color: '#475569'}}>客観的所見</span>
                                  </div>
                                  <div style={{
                                    background: '#f8fafc',
                                    padding: '10px 12px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    lineHeight: '1.6',
                                    color: '#334155',
                                    whiteSpace: 'pre-wrap'
                                  }}>
                                    {pastRecord.soap_objective}
                                  </div>
                                </div>
                              )}

                              {pastRecord.soap_assessment_plan && (
                                <div style={{marginBottom: '12px'}}>
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    marginBottom: '6px'
                                  }}>
                                    <span style={{
                                      background: '#f59e0b',
                                      color: '#fff',
                                      width: '20px',
                                      height: '20px',
                                      borderRadius: '4px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '11px',
                                      fontWeight: '700'
                                    }}>A/P</span>
                                    <span style={{fontSize: '12px', fontWeight: '600', color: '#475569'}}>評価・計画（A & P）</span>
                                  </div>
                                  <div style={{
                                    background: '#f8fafc',
                                    padding: '10px 12px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    lineHeight: '1.6',
                                    color: '#334155',
                                    whiteSpace: 'pre-wrap'
                                  }}>
                                    {pastRecord.soap_assessment_plan}
                                  </div>
                                </div>
                              )}

                              {pastRecord.doctor_notes && (
                                <div style={{marginBottom: '12px'}}>
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    marginBottom: '6px'
                                  }}>
                                    <span style={{fontSize: '14px'}}>📝</span>
                                    <span style={{fontSize: '12px', fontWeight: '600', color: '#475569'}}>医師メモ</span>
                                  </div>
                                  <div style={{
                                    background: '#fffbeb',
                                    padding: '10px 12px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    lineHeight: '1.6',
                                    color: '#92400e',
                                    border: '1px solid #fde68a',
                                    whiteSpace: 'pre-wrap'
                                  }}>
                                    {pastRecord.doctor_notes}
                                  </div>
                                </div>
                              )}

                              {/* 処方内容 */}
                              {pastRecord.prescriptions && pastRecord.prescriptions.length > 0 && (
                                <div>
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    marginBottom: '8px'
                                  }}>
                                    <span style={{fontSize: '14px'}}>💊</span>
                                    <span style={{fontSize: '12px', fontWeight: '600', color: '#475569'}}>処方内容</span>
                                  </div>
                                  <div style={{
                                    background: '#f0fdf4',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    border: '1px solid #bbf7d0'
                                  }}>
                                    {pastRecord.prescriptions.map((rx, rxIdx) => (
                                      <div 
                                        key={rxIdx}
                                        style={{
                                          padding: '8px 0',
                                          borderBottom: rxIdx < pastRecord.prescriptions.length - 1 ? '1px dashed #d1fae5' : 'none'
                                        }}
                                      >
                                        <div style={{fontWeight: '600', fontSize: '13px', color: '#166534'}}>
                                          {rx.drug_name}
                                        </div>
                                        {rx.count_text && (
                                          <div style={{fontSize: '12px', color: '#15803d', marginTop: '4px'}}>
                                            {rx.count_text}
                                          </div>
                                        )}
                                        {rx.instruction_text && (
                                          <div style={{fontSize: '12px', color: '#64748b', marginTop: '2px', fontStyle: 'italic'}}>
                                            {rx.instruction_text}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* からけん過去カルテ（旧システムからの移行データ） */}
                    {detail?.karaken_past_records && detail.karaken_past_records.length > 0 && (
                      <>
                        <div style={{ marginTop: '16px', marginBottom: '8px', padding: '8px 12px', background: '#faf5ff', borderRadius: '6px', border: '1px dashed #c084fc' }}>
                          <span style={{ fontSize: '12px', color: '#7c3aed', fontWeight: '600' }}>📚 からけん時代の診療記録</span>
                        </div>
                        {detail.karaken_past_records.map((kr, idx) => (
                          <div 
                            key={`karaken-${kr.reservation_id || idx}`}
                            style={{
                              border: '1px solid #e9d5ff',
                              borderRadius: '12px',
                              overflow: 'hidden',
                              background: '#fefbff'
                            }}
                          >
                            {/* からけんカルテヘッダー */}
                            <div style={{
                              background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
                              padding: '12px 16px',
                              borderBottom: '1px solid #e9d5ff',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: '8px'
                            }}>
                              <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
                                <span style={{fontSize: '16px'}}>🗓️</span>
                                <span style={{fontWeight: '600', color: '#581c87', fontSize: '14px'}}>
                                  {kr.date ? new Date(kr.date).toLocaleDateString('ja-JP') : '-'}
                                </span>
                                <span style={{
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  background: kr.consultation_mode === 'first_visit' ? '#fef3c7' : '#dbeafe',
                                  color: kr.consultation_mode === 'first_visit' ? '#92400e' : '#1e40af'
                                }}>
                                  {kr.consultation_mode === 'first_visit' ? '初診' : '再診'}
                                </span>
                                <span style={{
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  fontWeight: '600',
                                  background: '#c4b5fd',
                                  color: '#4c1d95'
                                }}>
                                  からけん
                                </span>
                              </div>
                              <div style={{fontSize: '12px', color: '#7c3aed'}}>
                                担当: {kr.doctor_name || '-'}
                              </div>
                            </div>

                            {/* 疾患名 */}
                            <div style={{
                              padding: '8px 16px',
                              borderBottom: '1px solid #f3e8ff',
                              background: '#fefbff'
                            }}>
                              <span style={{fontSize: '13px', color: '#7c3aed'}}>主疾患: </span>
                              <span style={{fontSize: '13px', fontWeight: '600', color: '#581c87'}}>{kr.disease_name || '-'}</span>
                            </div>

                            {/* 処方内容 */}
                            {kr.prescriptions && kr.prescriptions.length > 0 && (
                              <div style={{padding: '16px'}}>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  marginBottom: '8px'
                                }}>
                                  <span style={{fontSize: '14px'}}>💊</span>
                                  <span style={{fontSize: '12px', fontWeight: '600', color: '#7c3aed'}}>処方内容</span>
                                </div>
                                <div style={{
                                  background: '#f5f3ff',
                                  padding: '12px',
                                  borderRadius: '8px',
                                  border: '1px solid #e9d5ff'
                                }}>
                                  {kr.prescriptions.map((rx, rxIdx) => (
                                    <div 
                                      key={rxIdx}
                                      style={{
                                        padding: '8px 0',
                                        borderBottom: rxIdx < kr.prescriptions.length - 1 ? '1px dashed #e9d5ff' : 'none'
                                      }}
                                    >
                                      <div style={{fontWeight: '600', fontSize: '13px', color: '#6d28d9'}}>
                                        {rx.medication_name || rx.display_text}
                                      </div>
                                      {(rx.dosage || rx.days || rx.usage1) && (
                                        <div style={{fontSize: '12px', color: '#7c3aed', marginTop: '4px'}}>
                                          {rx.dosage && `${rx.dosage}錠`}
                                          {rx.days && ` ${rx.days}日分`}
                                          {rx.usage1 && ` / ${rx.usage1}`}
                                          {rx.usage2 && ` ${rx.usage2}`}
                                        </div>
                                      )}
                                      {rx.free_text && (
                                        <div style={{fontSize: '11px', color: '#8b5cf6', marginTop: '2px', fontStyle: 'italic'}}>
                                          {rx.free_text}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </>
                    )}
                  </div>
              </div>
              {/* 処方歴セクション */}
              <div className="medichat-patient-section">
                <div className="dr-section-title">💊 処方歴</div>
                  <div className="medichat-patient-grid" style={{marginTop: '10px'}}>
                    {Array.isArray(detail?.do_prescription) && detail.do_prescription.length > 0 && coverageKindLive === 'insurance' && (
                      <div style={{gridColumn: '1 / -1', marginBottom: '8px', display: 'flex', justifyContent: 'flex-end'}}>
                        <button
                          type="button"
                          className="medichat-add-medicine-btn"
                          onClick={applyDoPrescription}
                        >
                          Do処方を適用
                        </button>
                      </div>
                    )}
                    {loadingPrescriptionHistory ? (
                      <div style={{textAlign: 'center', padding: '20px', color: '#64748b'}}>
                        <i className="fas fa-spinner fa-spin" style={{fontSize: '24px', marginBottom: '8px'}}></i>
                        <p>処方歴を読み込んでいます...</p>
                      </div>
                    ) : prescriptionHistory ? (
                      <>
                        {/* 主疾患の処方歴 */}
                        <div style={{gridColumn: '1 / -1', marginBottom: '16px'}}>
                          <h4 style={{fontSize: '15px', fontWeight: '600', color: '#1e293b', marginBottom: '12px', paddingBottom: '8px', borderBottom: '2px solid #e2e8f0'}}>
                            🎯 {detail?.primary_disease?.name || '主疾患'}で過去に処方された薬
                          </h4>
                          {prescriptionHistory.primary && prescriptionHistory.primary.length > 0 ? (
                            prescriptionHistory.primary.map((item, idx) => (
                              <div key={idx} style={{background: '#f8fafc', padding: '12px', borderRadius: '8px', marginBottom: '8px', borderLeft: '4px solid #667eea'}}>
                                <div style={{fontWeight: '600', color: '#1e293b', marginBottom: '6px'}}>
                                  {new Date(item.date).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' })}
                                </div>
                                <ul style={{listStyleType: 'none', paddingLeft: '20px', margin: 0}}>
                                  {item.medications.map((med, medIdx) => (
                                    <li key={medIdx} style={{color: '#334155', lineHeight: '1.6'}}>
                                      {med.drug_name}
                                      {med.dosage_info && ` - ${med.dosage_info}`}
                                      {med.memo && ` (${med.memo})`}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))
                          ) : (
                            <div style={{textAlign: 'center', color: '#94a3b8', padding: '16px'}}>処方歴がありません</div>
                          )}
                        </div>

                        {/* その他の疾患の処方歴 */}
                        <div style={{gridColumn: '1 / -1'}}>
                          <h4 style={{fontSize: '15px', fontWeight: '600', color: '#1e293b', marginBottom: '12px', paddingBottom: '8px', borderBottom: '2px solid #e2e8f0'}}>
                            📋 その他の診察で過去に処方された薬
                          </h4>
                          {prescriptionHistory.other && prescriptionHistory.other.length > 0 ? (
                            prescriptionHistory.other.map((item, idx) => (
                              <div key={idx} style={{background: '#f8fafc', padding: '12px', borderRadius: '8px', marginBottom: '8px', borderLeft: '4px solid #94a3b8'}}>
                                <div style={{display:'flex',justifyContent:'space-between'}}>
                                  <div style={{fontWeight: '600', color: '#1e293b', marginBottom: '4px'}}>
                                    {new Date(item.date).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' })}
                                  </div>
                                  <div style={{fontSize: '13px', color: '#64748b', marginBottom: '6px'}}>
                                    疾患: {item.disease_name}
                                  </div>
                                </div>
                                <ul style={{listStyleType: 'none', paddingLeft: '20px', margin: 0}}>
                                  {item.medications.map((med, medIdx) => (
                                    <li key={medIdx} style={{color: '#334155', lineHeight: '1.6'}}>
                                      {med.drug_name}
                                      {med.dosage_info && ` - ${med.dosage_info}`}
                                      {med.memo && ` (${med.memo})`}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))
                          ) : (
                            <div style={{textAlign: 'center', color: '#94a3b8', padding: '16px'}}>処方歴がありません</div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div style={{gridColumn: '1 / -1', textAlign: 'center', color: '#ef4444', padding: '16px'}}>
                        処方歴の読み込みに失敗しました
                      </div>
                    )}
                  </div>
              </div>
                    </div>
                  )}

                </div>{/* /dr-sub-content */}
              </>
            )}

            {/* ==============================
                カルテ入力 タブ
            ============================== */}
            {activeMainTab === 'karte' && (
              <>
                {/* カルテサブタブバー */}
                <div className="dr-sub-tabbar">
                  <button
                    type="button"
                    onClick={() => setActiveKarteSubTab('manual')}
                    className={`dr-sub-tab${activeKarteSubTab === 'manual' ? ' is-active' : ''}`}
                  >手動カルテ</button>
                  <button
                    type="button"
                    onClick={() => setActiveKarteSubTab('ai')}
                    className={`dr-sub-tab${activeKarteSubTab === 'ai' ? ' is-active' : ''}`}
                  >AI下書き</button>
                </div>

                {/* ---- 手動カルテサブタブ ---- */}
                {activeKarteSubTab === 'manual' && (
                  <div className="dr-sub-content">
              <div className="medichat-section">
                  <div className="medichat-section-title"><div className="medichat-section-icon">🏥</div>主疾患</div>

                  {/* 主疾患 or 症状（どちらか一方だけ表示） */}
                  {(primaryDiseaseDisplayName && String(primaryDiseaseDisplayName).trim() !== '')
                    ? (
                      // 主疾患がある：主疾患チップだけ出す（症状は出さない）
                      <div style={{ margin: '6px 0 10px' }}>
                        <span className="mcai-chip-v1">
                          患者が選択した疾患：{primaryDiseaseDisplayName}
                        </span>
                      </div>
                    ) : (
                      // 主疾患が無い：症状を出す
                      <div style={{fontSize:13, margin:'6px 0 10px', opacity:.95}}>
                        <div style={{marginBottom:6, display:'flex', flexWrap:'wrap', gap:'6px', alignItems:'center', justifyContent:'flex-start'}}>
                          <span style={{opacity:.7, marginRight:4}}>患者が選択した症状：</span>
                          {pickedSymptomNames.length
                            ? pickedSymptomNames.map((n,i)=>(
                                <span key={'s'+i} className="mcai-chip-v1" style={{marginTop:6}}>{n}</span>
                              ))
                            : <span style={{ marginLeft:6, opacity:.5}}>—</span>}
                        </div>
                      </div>
                    )
                  }


                  {/* 療養計画書必要の警告メッセージ（継続フォームかどうかも表示） */}
                  {detail?.needs_treatment_plan && (
                    <div style={{
                      margin: '8px 0 12px',
                      padding: '10px 12px',
                      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                      borderRadius: '8px',
                      border: '1px solid #f59e0b',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{ fontSize: '16px' }}>⚠️</span>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span style={{ fontSize: '13px', color: '#92400e', fontWeight: '600' }}>
                          当患者は療養計画書の作成が必要です
                        </span>
                        {/* 継続フォーム判定を表示 */}
                        <span style={{ fontSize: '11px', color: '#b45309' }}>
                          {detail?.patient_has_treatment_plan_history || detail?.patient_has_signed_treatment_plan_before
                            ? '📝 継続用フォームを使用してください'
                            : '📋 初回用フォームを使用してください'
                          }
                          {detail?.karaken_latest_careplan_signed_at && (
                            <span style={{ marginLeft: '8px', opacity: 0.8 }}>
                              （からけん最終サイン: {new Date(detail.karaken_latest_careplan_signed_at).toLocaleDateString('ja-JP')}）
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="select-wrapper-modern" style={{ maxWidth: 420 }}>
                    <label className="form-label-modern required">主疾患（※必須）</label>
                    {/* 既存の主疾患セレクト */}
                    <select
                      className="select-modern"
                      value={primaryDiseaseId || ''}
                      onChange={(e) => setPrimaryDiseaseId(e.target.value ? Number(e.target.value) : null)}
                      disabled={isOrdersLocked}
                      required
                    >
                      <option value="">疾患を選択してください</option>
                      {diseaseMaster.map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                    <div className="select-arrow">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                  <div style={{ marginTop: 8, fontSize: 13, opacity: .8 }}>
                    現在: <b>{coverageLabel}</b>（主疾患の選択に連動）
                  </div>
                </div>


            {/* SOAP */}
            <div className="medichat-section"><div className="medichat-section-title"><div className="medichat-section-icon">S</div>主観的症状</div>
              <textarea className="medichat-textarea" value={rec.soap_subjective || ''} readOnly={isCancelledDetail} onChange={(e)=>setRec(r=>({...r, soap_subjective:e.target.value}))}/>
            </div>
            <div className="medichat-section"><div className="medichat-section-title"><div className="medichat-section-icon">O</div>客観的所見</div>
              <textarea className="medichat-textarea" value={rec.soap_objective || ''} readOnly={isCancelledDetail} onChange={(e)=>setRec(r=>({...r, soap_objective:e.target.value}))}/>
            </div>
            <div className="medichat-section"><div className="medichat-section-title"><div className="medichat-section-icon">A/P</div>評価・計画</div>
              <textarea className="medichat-textarea" value={rec.soap_assessment_plan || ''} readOnly={isCancelledDetail} onChange={(e)=>setRec(r=>({...r, soap_assessment_plan:e.target.value}))}/>
            </div>
            <div className="medichat-section"><div className="medichat-section-title"><div className="medichat-section-icon">📝</div>医師メモ</div>
              <textarea className="medichat-textarea" value={rec.doctor_notes || ''} readOnly={isCancelledDetail} onChange={(e)=>setRec(r=>({...r, doctor_notes:e.target.value}))}/>
            </div>
                  </div>
                )}

                {/* ---- AI下書きサブタブ ---- */}
                {activeKarteSubTab === 'ai' && (
                  <div className="dr-sub-content">
                    {/* AI下書きセクション */}
                    {(!detail?.ai_consultation_records || detail.ai_consultation_records.length === 0) ? (
                      <div style={{textAlign: 'center', padding: '40px 20px', color: '#9ca3af'}}>
                        <div style={{fontSize: '32px', marginBottom: '12px'}}>🤖</div>
                        <div style={{fontSize: '14px', fontWeight: '600', marginBottom: '4px'}}>AI下書きはまだありません</div>
                        <div style={{fontSize: '12px'}}>ビデオ診察後に自動生成されます</div>
                      </div>
                    ) : (
                      <div>
                        {/* 複数件の場合だけ切り替えタブを出す */}
                        {detail.ai_consultation_records.length > 1 && (
                          <div style={{display: 'flex', gap: '6px', marginBottom: '12px', overflowX: 'auto', flexWrap: 'nowrap', paddingBottom: '4px'}}>
                            {detail.ai_consultation_records.map((record, index) => (
                              <button
                                key={record.id}
                                onClick={() => setActiveAiTab(index)}
                                style={{
                                  padding: '8px 14px',
                                  border: 'none',
                                  background: activeAiTab === index ? '#3b82f6' : '#f1f5f9',
                                  color: activeAiTab === index ? 'white' : '#64748b',
                                  borderRadius: '20px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  transition: 'all 0.2s',
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0,
                                }}
                              >
                                {record.is_adopted ? '✅ 採用済み' : (index === 0 ? '📝 下書き' : `#${index + 1}`)}
                                <span style={{fontSize: '10px', marginLeft: '4px', opacity: 0.8}}>
                                  {new Date(record.received_at).toLocaleString('ja-JP', {month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'})}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}

                        {/* レコードコンテンツ（編集可・一時保存/診察終了で保存） */}
                        {detail.ai_consultation_records.map((record, index) => {
                          const edit = getAiRecordEdit(record);
                          const subjectiveDirty = isAiSectionDirty(record, 'subjective', edit.s);
                          const objectiveDirty = isAiSectionDirty(record, 'objective', edit.o);
                          const assessmentDirty = isAiSectionDirty(record, 'assessment', edit.a);
                          const memoDirty = (record.ai_memo || '') !== (edit.memo || '');
                          const hasDirtyAiContent = subjectiveDirty || objectiveDirty || assessmentDirty || memoDirty;
                          const dirtyByKey = {
                            subjective: subjectiveDirty,
                            objective: objectiveDirty,
                            assessment: assessmentDirty,
                          };

                          return (
                          <div key={record.id} style={{display: activeAiTab === index ? 'block' : 'none'}}>
                            <div style={{background: '#f8fbff', padding: '16px', borderRadius: '8px', border: '1px solid #dbeafe', borderLeft: '4px solid #93c5fd'}}>
                              <div style={{marginBottom: '12px', fontSize: '13px', color: '#64748b', display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'flex-start', flexWrap: 'wrap'}}>
                                <div>
                                  <div style={{fontWeight: '700', color: '#9a5c00', marginBottom: '4px'}}>AI生成下書き</div>
                                  <div>
                                    <i className="fas fa-info-circle" style={{marginRight: '4px'}}></i>
                                    編集内容は一時保存または診察終了で保存されます
                                  </div>
                                  <div style={{marginTop: '4px'}}>
                                    確定は保存後に行ってください
                                  </div>
                                </div>
                                <div style={{display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end'}}>
                                  {hasUnconfirmedAiSection(record) ? (
                                    <button
                                      type="button"
                                      onClick={() => confirmAllAiSections(record.id)}
                                      disabled={confirmingAllAi || hasDirtyAiContent || isCancelledDetail}
                                      style={{
                                        padding: '8px 12px',
                                        borderRadius: '9999px',
                                        border: '1px solid #f0b35a',
                                        background: confirmingAllAi || hasDirtyAiContent ? '#fde7c2' : '#fff7ea',
                                        color: '#9a5c00',
                                        fontSize: '12px',
                                        fontWeight: '700',
                                        cursor: confirmingAllAi || hasDirtyAiContent ? 'default' : 'pointer',
                                      }}
                                    >
                                      {confirmingAllAi ? '確定中...' : hasDirtyAiContent ? '保存後に確定' : 'すべて確定'}
                                    </button>
                                  ) : (
                                    <span style={{fontSize: '12px', fontWeight: '700', color: '#17654b', background: '#ddf7ea', border: '1px solid #8dd7b4', borderRadius: '9999px', padding: '6px 10px'}}>
                                      すべて確定済み
                                    </span>
                                  )}
                                </div>
                                {record.received_at && (
                                  <span style={{marginLeft: '8px'}}>
                                    受信: {new Date(record.received_at).toLocaleString('ja-JP')}
                                  </span>
                                )}
                                {record.is_adopted && (
                                  <span style={{marginLeft: '8px', background: '#10b981', color: 'white', padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: '600'}}>
                                    ✓ 採用済み
                                  </span>
                                )}
                              </div>

                              {AI_SECTION_DEFINITIONS.map(({ key, label, title, editKey }) => {
                                const confirmation = getAiSectionConfirmation(record, key);
                                const isConfirming = confirmingAiSection === `${record.id}-${key}`;
                                const sectionDirty = dirtyByKey[key];
                                const currentValue = edit[editKey] || '';

                                return (
                                  <div key={key} style={{marginBottom: '16px', opacity: confirmation.confirmed && !sectionDirty ? 1 : 0.92}}>
                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap'}}>
                                      <h4 style={{fontSize: '14px', fontWeight: '600', color: '#1e293b', margin: 0}}>
                                        {label}（{title}）
                                      </h4>
                                      <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
                                        <span
                                          style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '2px 10px',
                                            borderRadius: '9999px',
                                            fontSize: '11px',
                                            fontWeight: '700',
                                            color: sectionDirty ? '#7a3b00' : confirmation.confirmed ? '#17654b' : '#8a6400',
                                            background: sectionDirty ? '#ffe4d9' : confirmation.confirmed ? '#ddf7ea' : '#fff0c9',
                                            border: `1px solid ${sectionDirty ? '#ffb08c' : confirmation.confirmed ? '#8dd7b4' : '#f0d082'}`,
                                          }}
                                        >
                                          {sectionDirty ? '未保存の変更あり' : confirmation.confirmed ? '確定済み' : '未確定'}
                                        </span>
                                        {!confirmation.confirmed && currentValue && (
                                          <button
                                            type="button"
                                            onClick={() => confirmAiSection(record.id, key)}
                                            disabled={isConfirming || sectionDirty || isCancelledDetail}
                                            style={{
                                              padding: '6px 10px',
                                              borderRadius: '9999px',
                                              border: '1px solid #f0b35a',
                                              background: isConfirming || sectionDirty ? '#fde7c2' : '#fff7ea',
                                              color: '#9a5c00',
                                              fontSize: '12px',
                                              fontWeight: '700',
                                              cursor: isConfirming || sectionDirty ? 'default' : 'pointer',
                                            }}
                                          >
                                            {isConfirming ? '確定中...' : sectionDirty ? '保存後に確定' : `${label}を確定`}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    <textarea
                                      className="medichat-textarea"
                                      value={currentValue}
                                      readOnly={isCancelledDetail}
                                      onChange={(e) => patchAiRecordEdit(record, { [editKey]: e.target.value })}
                                      rows={4}
                                      style={{
                                        background: confirmation.confirmed && !sectionDirty ? 'white' : '#fefcf7',
                                        border: confirmation.confirmed && !sectionDirty ? '1px solid #dbeafe' : '1px dashed #bfdbfe',
                                        lineHeight: 1.6,
                                        fontSize: '14px',
                                        color: '#334155',
                                      }}
                                    />
                                    {confirmation.confirmed_at && !sectionDirty && (
                                      <div style={{fontSize: '11px', color: '#17654b', marginTop: '6px'}}>
                                        確定日時: {new Date(confirmation.confirmed_at).toLocaleString('ja-JP')}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}

                              <div style={{marginBottom: '16px'}}>
                                <h4 style={{fontSize: '14px', fontWeight: '600', color: '#92400e', marginBottom: '8px'}}>
                                  📝 事務的要望（メモ）
                                  {memoDirty && (
                                    <span style={{marginLeft: '8px', fontSize: '11px', fontWeight: '700', color: '#7a3b00', background: '#ffe4d9', border: '1px solid #ffb08c', borderRadius: '9999px', padding: '2px 8px'}}>
                                      未保存の変更あり
                                    </span>
                                  )}
                                </h4>
                                <textarea
                                  className="medichat-textarea"
                                  value={edit.memo || ''}
                                  readOnly={isCancelledDetail}
                                  onChange={(e) => patchAiRecordEdit(record, { memo: e.target.value })}
                                  rows={3}
                                  style={{
                                    background: '#fffbeb',
                                    border: '1px solid #fde68a',
                                    lineHeight: 1.6,
                                    fontSize: '14px',
                                    color: '#92400e',
                                  }}
                                />
                              </div>

                              {/* 録音・文字起こしへのリンク */}
                              {(record.recording_url || record.transcript_url) && (
                                <div style={{display: 'flex', gap: '8px', marginTop: '12px'}}>
                                  {record.recording_url && (
                                    <a 
                                      href={record.recording_url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      style={{fontSize: '13px', color: '#3b82f6', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px'}}
                                    >
                                      <i className="fas fa-video"></i>
                                      録音データ
                                    </a>
                                  )}
                                  {record.transcript_url && (
                                    <a 
                                      href={record.transcript_url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      style={{fontSize: '13px', color: '#3b82f6', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px'}}
                                    >
                                      <i className="fas fa-file-alt"></i>
                                      文字起こしデータ
                                    </a>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* ==============================
                オーダー タブ
            ============================== */}
            {activeMainTab === 'order' && (
              <div className="dr-sub-content">
            {isPostFinishDetail && (
              <div style={{marginBottom: '12px', padding: '12px 14px', background: '#fff7ea', borderRadius: '8px', border: '1px solid #f0d082', fontSize: '13px', color: '#9a5c00', fontWeight: 600}}>
                診察終了後はオーダーを変更できません。カルテ本文のみ「保存」で更新できます。
              </div>
            )}
            {/* 薬オーダー（主疾患に応じてリアルタイム切替） */}
            <div className="medichat-section medichat-medicine-section" style={isOrdersLocked ? { pointerEvents: 'none', opacity: 0.72 } : undefined}>
              <div className="medichat-medicine-header">
                <div className="medichat-section-title"><div className="medichat-section-icon">💊</div>{coverageKindLive === 'self_pay' ? '自費薬オーダー' : '処方薬オーダー'}</div>
                {!isOrdersLocked && (coverageKindLive === 'self_pay' ? (
                  <button className="medichat-add-medicine-btn" onClick={()=>addSelfPayRow()}>＋ 自費薬を追加</button>
                ) : (
                  <>
                    <button className="medichat-add-medicine-btn" onClick={()=>addOrderRow()}>＋ 薬を追加</button>
                    {prescriptionSetFeatureEnabled === true ? (
                      <button className="medichat-add-medicine-btn" onClick={() => openPrescriptionSetModal()}>＋ 処方セット追加</button>
                    ) : (
                      <span className="medichat-inline-hint">処方セット機能は現在無効です。</span>
                    )}
                  </>
                ))}
              </div>

              {coverageKindLive === 'self_pay' ? (
                <div className="mcsp-list-react">
                  {selfPayRows.length === 0 ? (
                    <div className="medichat-empty-state">自費薬は未選択です。<br/>「自費薬を追加」ボタンから追加してください。</div>
                  ) : (
                    selfPayRows.map((row, idx) => (
                      <SelfPayRow key={idx}
                        valueId={row.catalog_item_id} valueQty={row.quantity}
                        options={selfPayMeds}
                        onChange={patch => updateSelfPayRow(idx, patch)}
                        onRemove={()=>removeSelfPayRow(idx)}
                      />
                    ))
                  )}
                </div>
              ) : (
                <div className="medichat-medicine-list">
                  {orderRows.length === 0 ? (
                    <div className="medichat-empty-state">まだ処方薬がありません<br/>「薬を追加」ボタンから追加してください</div>
                  ) : (
                    orderRows.map((row, idx) => {
                      // 用法②が不要なグループ（フリー入力のみ）
                      const freeInputOnlyGroups = ['NASAL', 'OPHTHALMIC', 'INHALATION', 'PATCH', 'TOPICAL', 'GARGLE', 'EXTERNAL', 'SUBCUTANEOUS', 'INTRAVENOUS', 'OTHER'];
                      const isFreeInputOnly = freeInputOnlyGroups.includes(row.usage_group);
                      // 外用薬・注射薬のグループ（日数フィールドを非表示にする）
                      const externalInjectionGroups = ['NASAL', 'OPHTHALMIC', 'INHALATION', 'PATCH', 'TOPICAL', 'GARGLE', 'EXTERNAL', 'SUBCUTANEOUS', 'INTRAVENOUS'];
                      const isExternalOrInjection = externalInjectionGroups.includes(row.usage_group);
                      // 頓服薬かどうかを判定
                      const isAsNeeded = row.usage_group === 'AS_NEEDED';
                      
                      const childOptions = (() => {
                        if (!row.usage_group) return [];
                        if (isFreeInputOnly) return []; // フリー入力のみの場合は選択肢なし
                        if (row.usage_group === 'AS_NEEDED') return patterns.filter(p => p.default_as_needed);
                        const n = dosesFromGroup(row.usage_group);
                        return patterns.filter(p => !p.default_as_needed && p.doses_per_day === n);
                      })();
                      const selectedPat = row.dosage_pattern_id ? patternIndex.get(Number(row.dosage_pattern_id)) : null;
                      return (
                        <div key={idx} className="medichat-medicine-item">
                          <div className="medichat-medicine-grid">
                            <div className="medichat-form-field">
                              <label>薬品名</label>
                              <MedicationSearchInput
                                valueId={row.catalog_item_id}
                                isFirstVisit={isFirstVisit}
                                valueLabel={row.catalog_item_label}
                                onPick={(it) => updateOrderRow(idx, { 
                                  catalog_item_id: it.id, 
                                  catalog_item_label: it.name,
                                  normalized_amount_per_unit: it.normalized_amount_per_unit || 1,
                                  medication_unit: it.medication_unit || '錠'
                                })}
                              />
                            </div>
                            <div className="medichat-medicine-row medichat-medicine-row--top">
                            <div className="medichat-form-field medichat-form-field--usage1">
                              <label>用法①</label>
                              <select value={row.usage_group || ''} onChange={(e)=>onChangeParentGroup(idx, e.target.value)}>
                                <option value="">選択</option>
                                {PARENT_GROUPS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                              </select>
                            </div>
                            <div className="medichat-form-field medichat-form-field--usage2">
                              <label>用法②（詳細）</label>
                              {isFreeInputOnly ? (
                                <select value="free_input" disabled>
                                  <option value="free_input">フリー入力</option>
                                </select>
                              ) : (
                                <select value={row.dosage_pattern_id || ''} onChange={(e)=>onChangeChildPattern(idx, e.target.value)} disabled={!row.usage_group}>
                                  <option value="">{row.usage_group ? '選択してください' : '先に用法①を選択'}</option>
                                  {childOptions.map(p => <option key={p.id} value={p.id}>{p.instruction_text}</option>)}
                                </select>
                              )}
                              <div className="medichat-field-hint">{isFreeInputOnly ? '' : (selectedPat ? `選択中: ${selectedPat.instruction_text}` : '')}</div>
                            </div>
                            </div>
                            <div className="medichat-medicine-row medichat-medicine-row--bottom">
                            <div className={`medichat-form-field medichat-form-field--amount ${isExternalOrInjection ? 'is-full' : ''}`}>
                                <label>{isExternalOrInjection ? '個数' : isAsNeeded ? '1回量' : '1日量'}</label>
                                <div className="medichat-inline-input-group">
                                {/* 1日の合計錠数を入力（doses_per_day(分○)は表示用のみ） */}
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  pattern="[0-9]*\.?[0-9]{0,2}"
                                  className={`medichat-amount-input ${isExternalOrInjection ? 'is-external' : ''}`}
                                  value={row.tablets_per_dose || ''}
                                  onChange={(e) => {
                                    const raw = normalizeDoseDraftInput(e.target.value);
                                    if (raw === '' || isDoseDraftInput(raw)) {
                                      updateOrderRow(idx, { tablets_per_dose: raw });
                                    }
                                  }}
                                  onBlur={(e) => {
                                    const normalized = normalizeDoseNumber(e.target.value);
                                    updateOrderRow(idx, { tablets_per_dose: normalized });
                                  }}
                                  />
                                   {row.normalized_amount_per_unit && row.medication_unit && (
                                     <span className="medichat-inline-unit-label">
                                       ×{row.normalized_amount_per_unit}{row.medication_unit}
                                     </span>
                                   )}
                                   </div>
                              </div>
                              {!isExternalOrInjection && (
                              <div className="medichat-form-field medichat-form-field--days">
                                <label>{isAsNeeded ? '回数' : '日数'} <span className="medichat-field-subhint">{isAsNeeded ? '' : (isFirstVisit ? '初診: 最大30日' : '再診: 最大90日')}</span></label>
                                <div className="medichat-inline-input-group">
                                <input
                                  type="number"
                                  inputMode="numeric"
                                  pattern="\\d*"
                                  min="0"
                                  className="medichat-days-input"
                                  max={isAsNeeded ? MAX_AS_NEEDED_DAYS : daysMax}
                                  value={row.days ?? ''}         // ← 文字列のまま管理、空文字も許容
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    const maxVal = isAsNeeded ? MAX_AS_NEEDED_DAYS : daysMax;

                                    // 1) 空はそのまま（削除できるようにする）
                                    if (v === '') {
                                      updateOrderRow(idx, { days: '' });
                                      return;
                                    }

                                    // 2) 数字以外は弾く（ブラウザ依存の入力を保険で抑止）
                                    if (!/^\d+$/.test(v)) return;

                                    // 3) 上限を超えたら即クランプ
                                    const n = Number(v);
                                    if (n > maxVal) {
                                      updateOrderRow(idx, { days: String(maxVal) });
                                    } else {
                                      updateOrderRow(idx, { days: v }); // ← 数字文字列のまま
                                    }
                                  }}
                                  onBlur={(e) => {
                                    // フォーカスアウト時に最終クリーンアップ（先頭ゼロ等の統一）
                                    const v = e.target.value;
                                    if (v === '') return; // 空は空のまま（送信時に null へ変換）
                                    const maxVal = isAsNeeded ? MAX_AS_NEEDED_DAYS : daysMax;
                                    const n = Math.min(maxVal, Math.max(0, Number(v)));
                                    updateOrderRow(idx, { days: String(n) });
                                  }}
                                />
                                  <span className="medichat-inline-unit-label">{isAsNeeded ? '回分' : '日分'}</span>
                                  </div>
                              </div>
                              )}
                            </div>
                            {BODY_PART_REQUIRED_GROUPS.includes(row.usage_group) && (
                              <div className="medichat-form-field">
                                <label>使用部位 <span className="medichat-required-asterisk">*</span></label>
                                <input
                                  type="text"
                                  placeholder="例: 両目、左耳、患部"
                                  value={row.body_part || ''}
                                  onChange={(e) => updateOrderRow(idx, { body_part: e.target.value })}
                                />
                              </div>
                            )}
                            <div className="medichat-form-field">
                              <label>メモ</label>
                              <input type="text" value={row.memo} onChange={(e)=>updateOrderRow(idx, { memo: e.target.value })}/>
                            </div>
                          </div>
                          <button className="medichat-remove-btn medichat-remove-btn--floating" onClick={()=>removeOrderRow(idx)}>X</button>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>

          {coverageKindLive === 'insurance' && (
            <div className="medichat-section" style={isOrdersLocked ? { pointerEvents: 'none', opacity: 0.72 } : undefined}>
              <div className="medichat-medicine-header">
                <div className="medichat-section-title">
                  <div className="medichat-section-icon">📄</div>書類
                </div>
                {!isOrdersLocked && (
                  <button className="medichat-add-medicine-btn" onClick={()=>addDocRow()}>＋ 書類を追加</button>
                )}
              </div>
              {docRows.length === 0 ? (
                <div className="medichat-empty-state">書類は未選択です。「書類を追加」から選択してください。</div>
              ) : (
                <div className="mcsp-list-react">
                  {docRows.map((row, idx) => (
                    <div key={idx} className="mcsp-row-react medichat-doc-row">
                      <div className="medichat-doc-row-main">
                        {(() => {
                          const missing = getMissingRequiredDocumentFields(row);
                          if (missing.length === 0) return null;
                          return (
                            <span
                              className="medichat-doc-unfinished-badge"
                              title={`未入力: ${missing.map(k => DOC_FIELD_LABELS[k] || k).join('、')}`}
                            >
                              未完了
                            </span>
                          );
                        })()}
                        <select
                          className="medichat-doc-select"
                          value={row.catalog_item_id || ''}
                          // 書類セクション内の select onChange を以下に変更
                          onChange={e => {
                            const val = e.target.value ? Number(e.target.value) : null;
                            updateDocRow(idx, { catalog_item_id: val });
                            const treatmentPlanId = detail?.treatment_plan_catalog_item?.id;
                            if (
                              val &&
                              treatmentPlanId &&
                              Number(val) === Number(treatmentPlanId) &&
                              selectedSessionId &&
                              typeof window !== 'undefined'
                            ) {
                              sessionStorage.removeItem(`mcTreatmentPlanRemoved_${selectedSessionId}`);
                            }
                            if (val) {
                              const selectedDoc = docMaster.find(x => Number(x.id) === val) || null;
                              // 直ちにモーダル表示（プリフィルはモーダルで実施）
                              setDocModalTarget({ idx, selectedDoc });
                              setDocModalOpen(true);
                            }
                          }}
                        >
                          <option value="">選択してください</option>
                          {docMaster
                            // 初診の場合は傷病手当金申請書（DOC_DISABILITY）・療養計画書（LIFESTYLE_DOCUMENT）を除外（初診では発行不可）
                            .filter(it => !(detail?.consultation_mode === 'first_visit' && (it.code === 'DOC_DISABILITY' || it.code === 'LIFESTYLE_DOCUMENT')))
                            .map(it => (
                            <option key={it.id} value={it.id}>
                                {it.name} {it.points>0 ? `（${it.points}点）` : `（¥${(it.price_yen ?? it.total_yen ?? 0).toLocaleString()}）`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        type="button"
                        className="medichat-secondary-btn medichat-doc-edit-btn"
                        disabled={!row.catalog_item_id}
                        onClick={() => {
                          const selectedDoc = docMaster.find(x => Number(x.id) === Number(row.catalog_item_id)) || null;
                          if (!selectedDoc) return;
                          setDocModalTarget({ idx, selectedDoc });
                          setDocModalOpen(true);
                        }}
                      >
                        情報入力
                      </button>
                      <button type="button" className="mcsp-remove-react medichat-remove-btn" onClick={()=>removeDocRow(idx)}>削除</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
           )}

            {/* 器材セクション（保険検査・自費検査・針を統一） */}
            <div className="medichat-section" style={isOrdersLocked ? { pointerEvents: 'none', opacity: 0.72 } : undefined}>
              <div className="medichat-medicine-header">
                <div className="medichat-section-title">
                  <div className="medichat-section-icon">🔧</div>器材
                </div>
                {!isOrdersLocked && (
                  <button className="medichat-add-medicine-btn" onClick={addEquipmentRow}>＋ 器材を追加</button>
                )}
              </div>
              {equipmentRows.length === 0 ? (
                <div className="medichat-empty-state">器材は未選択です。「器材を追加」から選択してください。</div>
              ) : (
                <div className="mcsp-list-react">
                  {equipmentRows.map((row, idx) => (
                    <EquipmentRow
                      key={idx}
                      row={row}
                      allOptions={coverageKindLive === 'self_pay'
                        ? { exam_insurance: [], exam_self_pay: examMasters.exam_self_pay, needles: examMasters.needles }
                        : examMasters
                      }
                      onChange={patch => updateEquipmentRow(idx, patch)}
                      onRemove={() => removeEquipmentRow(idx)}
                    />
                  ))}
                </div>
              )}
            </div>
              </div>
            )}
          </>
        ) : <div className="medichat-error">データが見つかりません</div>}
        </div>{/* /dr-review-body */}


      {/* ===== フローティングアクションボタン ===== */}
      {detail && (
        <div className="dr-fab-container">
          {fabOpen && (
            <div className="dr-fab-overlay" onClick={() => setFabOpen(false)} />
          )}
          {fabOpen && (
            <div className="dr-fab-menu">
              {isCancelledDetail ? (
                <button
                  type="button"
                  className="dr-fab-menu-item dr-fab-menu-item--undo-cancel"
                  onClick={() => { setFabOpen(false); undoCancel(); }}
                  disabled={cancelling}
                >
                  <span className="dr-fab-menu-icon">✓</span>
                  <span className="dr-fab-menu-label">{cancelling ? '取り消し中...' : 'キャンセルを取り消す'}</span>
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="dr-fab-menu-item dr-fab-menu-item--bot"
                    onClick={() => { setFabOpen(false); setDoctorBotOpen(true); }}
                  >
                    <span className="dr-fab-menu-icon">💬</span>
                    <span className="dr-fab-menu-label">医療FAQ</span>
                  </button>
                  <button
                    type="button"
                    className="dr-fab-menu-item dr-fab-menu-item--next"
                    onClick={() => { setFabOpen(false); setNextApptModalOpen(true); }}
                  >
                    <span className="dr-fab-menu-icon">📅</span>
                    <span className="dr-fab-menu-label">次回予約</span>
                  </button>
                  {!isPostFinishDetail && (
                    <button
                      type="button"
                      className="dr-fab-menu-item dr-fab-menu-item--submit"
                      onClick={() => { setFabOpen(false); submitAll(); }}
                      disabled={submitting || !canSubmitReview}
                      style={{opacity: canSubmitReview ? 1 : 0.6}}
                    >
                      <span className="dr-fab-menu-icon">✅</span>
                      <span className="dr-fab-menu-label">{submitting ? '送信中...' : '診察終了'}</span>
                    </button>
                  )}
                  <button
                    type="button"
                    className="dr-fab-menu-item dr-fab-menu-item--draft"
                    onClick={() => { setFabOpen(false); saveDraft(); }}
                    disabled={submitting}
                  >
                    <span className="dr-fab-menu-icon">💾</span>
                    <span className="dr-fab-menu-label">
                      {submitting ? '保存中...' : (isPostFinishDetail ? '保存' : '一時保存')}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="dr-fab-menu-item dr-fab-menu-item--cancel"
                    onClick={() => { setFabOpen(false); setCancelModalOpen(true); }}
                  >
                    <span className="dr-fab-menu-icon">✕</span>
                    <span className="dr-fab-menu-label">キャンセル</span>
                  </button>
                </>
              )}
            </div>
          )}
          <button
            type="button"
            className={`dr-fab-trigger${fabOpen ? " is-open" : ""}`}
            onClick={() => setFabOpen(v => !v)}
            aria-label="アクションメニュー"
          >
            {fabOpen ? (
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.5 1.5L16.5 16.5M16.5 1.5L1.5 16.5" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 2V18M2 10H18" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            )}
          </button>
        </div>
      )}

      {/* ===== ボトムナビゲーション ===== */}
      {/* ボトムナビゲーション — ロード前も常時表示 */}
      {(() => {
        const orderBadge = (orderRows?.length || 0) + (selfPayRows?.length || 0) + (docRows?.length || 0) + (equipmentRows?.length || 0);
        const validOrderBadge = coverageKindLive === 'self_pay'
          ? selfPayRows.filter(r => r.catalog_item_id).length + docRows.filter(r => r.catalog_item_id).length + equipmentRows.filter(r => r.catalog_item_id).length
          : orderRows.filter(r => r.catalog_item_id).length + docRows.filter(r => r.catalog_item_id).length + equipmentRows.filter(r => r.catalog_item_id).length;
        return (
          <nav className="dr-bottom-nav">
            <button
              type="button"
              className={`dr-nav-item${activeMainTab === 'info' ? ' is-active' : ''}`}
              onClick={() => setActiveMainTab('info')}
            >
              <span className="dr-nav-icon">🗂</span>
              <span className="dr-nav-label">基本情報</span>
            </button>
            <button
              type="button"
              className={`dr-nav-item${activeMainTab === 'karte' ? ' is-active' : ''}`}
              onClick={() => setActiveMainTab('karte')}
            >
              <span className="dr-nav-icon">📋</span>
              <span className="dr-nav-label">カルテ入力</span>
            </button>
            <button
              type="button"
              className={`dr-nav-item${activeMainTab === 'order' ? ' is-active' : ''}`}
              onClick={() => setActiveMainTab('order')}
            >
              <span className="dr-nav-icon-wrap">
                <span className="dr-nav-icon">💊</span>
                {validOrderBadge > 0 && (
                  <span className="dr-nav-badge">{validOrderBadge}</span>
                )}
              </span>
              <span className="dr-nav-label">オーダー</span>
            </button>
          </nav>
        );
      })()}

      {docModalOpen && (
      <DocumentEditorModalV2Mobile
        open={docModalOpen}
        onClose={()=>{ setDocModalOpen(false); setDocModalTarget({ idx:null, selectedDoc:null }); }}
        onSave={(fieldValues)=>{
          const { idx } = docModalTarget;
          if (idx != null) {
            updateDocRow(idx, { field_values: fieldValues });
          }
          setDocModalOpen(false);
          setDocModalTarget({ idx:null, selectedDoc:null });
        }}
        selectedDoc={docModalTarget.selectedDoc}
        patient={detail?.patient || {}}
        primaryDiseaseDisplayName={primaryDiseaseDisplayName}
           initialFieldValues={
               docModalTarget.idx != null ? (docRows[docModalTarget.idx]?.field_values || {}) : {}
           }
      />
    )}
    {nextApptModalOpen && (
      <NextAppointmentModal
        open={nextApptModalOpen}
        onClose={() => setNextApptModalOpen(false)}
        sessionId={selectedSessionId}
        patientName={detail?.patient?.name || '患者'}
        primaryDiseaseId={primaryDiseaseId}
        primaryDiseaseName={selectedDisease?.name || primaryDiseaseDisplayName}
        primaryDiseaseInsuranceCovered={selectedDisease ? !!selectedDisease.insurance_covered : null}
      />
    )}

    {doctorBotOpen && (
      <div
        className="mdbot-overlay-v1"
        role="dialog"
        aria-modal="true"
        aria-label="医療FAQチャット"
        onClick={() => setDoctorBotOpen(false)}
      >
        <div className="mdbot-modal-v1" onClick={(e) => e.stopPropagation()}>
          <div className="mdbot-header-v1">
            <div>
              <div className="mdbot-title-v1">💬 医療FAQチャット</div>
              <div className="mdbot-subtitle-v1">問診・患者情報を踏まえた医師向け補助回答です</div>
            </div>
            <button
              type="button"
              className="mdbot-close-v1"
              aria-label="医療FAQチャットを閉じる"
              onClick={() => setDoctorBotOpen(false)}
            >
              ×
            </button>
          </div>
          <div className="mdbot-body-v1" ref={doctorBotThreadRef}>
            {doctorBotMessages.map((message, idx) => (
              <div key={`${message.role}-${idx}`} className={`mdbot-line-v1 ${message.role}`}>
                <div className="bubble">{message.text}</div>
              </div>
            ))}
            {doctorBotLoading && (
              <div className="mdbot-line-v1 bot">
                <div className="bubble">回答を作成しています...</div>
              </div>
            )}
          </div>
          <form className="mdbot-input-v1" onSubmit={sendDoctorBotMessage}>
            <textarea
              rows={2}
              value={doctorBotInput}
              onChange={(e) => setDoctorBotInput(e.target.value)}
              placeholder="薬の注意点、鑑別、治療方針などを質問"
              disabled={doctorBotLoading}
            />
            <button type="submit" disabled={doctorBotLoading || !doctorBotInput.trim()}>
              送信
            </button>
          </form>
        </div>
      </div>
    )}

    {/* ファイル閲覧モーダル */}
    <FileViewerModal
      open={fileViewerOpen}
      onClose={() => setFileViewerOpen(false)}
      files={viewerFiles}
      title={viewerTitle}
    />

    {prescriptionSetModalOpen && (
      <div
        onClick={() => setPrescriptionSetModalOpen(false)}
        style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(15,23,42,0.5)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'white',
            borderRadius: '20px 20px 0 0',
            width: '100%',
            maxWidth: '600px',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 -4px 40px rgba(0,0,0,0.16)',
          }}
        >
          {/* ヘッダー */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 20px 12px',
            borderBottom: '1px solid #f1f5f9',
            flexShrink: 0,
          }}>
            <div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', marginBottom: '2px' }}>
                処方セット追加
              </div>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                タップして処方に反映されます
              </div>
            </div>
            <button
              onClick={() => setPrescriptionSetModalOpen(false)}
              style={{
                width: '32px', height: '32px', borderRadius: '50%',
                background: '#f1f5f9', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '14px', color: '#64748b',
              }}
            >
              ✕
            </button>
          </div>

          {/* コンテンツ */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px 20px' }}>
            {prescriptionSetLoading ? (
              <div style={{ textAlign: 'center', padding: '32px', color: '#94a3b8', fontSize: '14px' }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
                読み込み中...
              </div>
            ) : (personalPrescriptionSetList.length === 0 && sharedPrescriptionSetList.length === 0) ? (
              <div style={{ textAlign: 'center', padding: '32px', color: '#94a3b8', fontSize: '14px' }}>
                <div style={{ fontSize: '28px', marginBottom: '8px' }}>💊</div>
                利用可能な処方セットがありません
              </div>
            ) : (
              <>
                {personalPrescriptionSetList.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{
                      fontSize: '11px', fontWeight: '700', color: '#64748b',
                      textTransform: 'uppercase', letterSpacing: '0.06em',
                      marginBottom: '8px', padding: '0 4px',
                    }}>
                      個人処方セット
                    </div>
                    {personalPrescriptionSetList.map((s) => (
                      <button
                        key={s.id}
                        disabled={prescriptionSetLoading}
                        onClick={async () => { await applyPrescriptionSet(Number(s.id)); }}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          background: selectedPrescriptionSetId === String(s.id) ? '#f0fdf4' : 'white',
                          border: selectedPrescriptionSetId === String(s.id) ? '2px solid #22c55e' : '1px solid #e2e8f0',
                          borderRadius: '12px',
                          padding: '14px 16px',
                          marginBottom: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ fontWeight: '600', fontSize: '14px', color: '#0f172a' }}>{s.title}</div>
                          <div style={{ fontSize: '12px', color: '#64748b', flexShrink: 0, marginLeft: '8px' }}>
                            💊 {s.item_count ?? 0}種類
                          </div>
                        </div>
                        {Array.isArray(s.medication_preview) && s.medication_preview.length > 0 && (
                          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {s.medication_preview.join('・')}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {sharedPrescriptionSetList.length > 0 && (
                  <div>
                    <div style={{
                      fontSize: '11px', fontWeight: '700', color: '#64748b',
                      textTransform: 'uppercase', letterSpacing: '0.06em',
                      marginBottom: '8px', padding: '0 4px',
                    }}>
                      共通処方セット
                    </div>
                    {sharedPrescriptionSetList.map((s) => (
                      <button
                        key={s.id}
                        disabled={prescriptionSetLoading}
                        onClick={async () => { await applyPrescriptionSet(Number(s.id)); }}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          background: selectedPrescriptionSetId === String(s.id) ? '#eff6ff' : '#fafbff',
                          border: selectedPrescriptionSetId === String(s.id) ? '2px solid #3b82f6' : '1px solid #dbeafe',
                          borderRadius: '12px',
                          padding: '14px 16px',
                          marginBottom: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontWeight: '600', fontSize: '14px', color: '#0f172a' }}>{s.title}</span>
                            <span style={{
                              fontSize: '10px', fontWeight: '700', padding: '1px 6px',
                              background: '#dbeafe', color: '#1e40af', borderRadius: '9999px',
                            }}>共通</span>
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b', flexShrink: 0, marginLeft: '8px' }}>
                            💊 {s.item_count ?? 0}種類
                          </div>
                        </div>
                        {Array.isArray(s.medication_preview) && s.medication_preview.length > 0 && (
                          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {s.medication_preview.join('・')}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* フッター */}
          <div style={{
            padding: '12px 16px',
            borderTop: '1px solid #f1f5f9',
            background: '#fafafa',
            flexShrink: 0,
          }}>
            <button
              onClick={() => setPrescriptionSetModalOpen(false)}
              style={{
                width: '100%', padding: '13px',
                border: '1px solid #e2e8f0', borderRadius: '10px',
                background: 'white', fontSize: '14px', color: '#475569',
                cursor: 'pointer', fontWeight: '500',
              }}
            >
              閉じる
            </button>
          </div>
        </div>
      </div>
    )}

    {/* キャンセルモーダル */}
    {cancelModalOpen && (
      <div className="modal_overlay" onClick={() => setCancelModalOpen(false)}>
        <div className="modal_content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '420px', width: '90%'}}>
          <button className="close_button" onClick={() => setCancelModalOpen(false)}>✖︎</button>
          <div className="modal_icon warning_icon" style={{background: '#ef4444'}}>
            <span style={{fontSize: '36px'}}>⚠️</span>
          </div>
          <p className="modal_title">診察をキャンセル</p>
          <p className="modal_subtitle">この操作は取り消せません</p>
          
          <div style={{marginBottom: '16px', textAlign: 'left', width: '100%'}}>
            <label style={{display: 'block', marginBottom: '8px', fontWeight: '600', color: '#475569'}}>
              キャンセル理由
            </label>
            <select 
              value={cancelReason} 
              onChange={(e) => setCancelReason(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            >
              <option value="">選択してください</option>
              <option value="診察不能">診察不能</option>
              <option value="無断キャンセル">無断キャンセル</option>
              <option value="運営都合">運営都合</option>
            </select>
          </div>
          
          <div style={{marginBottom: '20px', textAlign: 'left', width: '100%'}}>
            <label style={{display: 'block', marginBottom: '8px', fontWeight: '600', color: '#475569'}}>
              備考（任意）
            </label>
            <textarea 
              value={cancelNote}
              onChange={(e) => setCancelNote(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
              placeholder="キャンセルの詳細や患者への連絡事項など"
            />
          </div>
          
          <div className="multi_button">
            <button 
              className="multi_button_left" 
              style={{border:"1px solid lightgrey"}}
              onClick={() => {
                setCancelModalOpen(false);
                setCancelReason('');
                setCancelNote('');
              }}
            >
              戻る
            </button>
            <button 
              className="multi_button_right" 
              style={{background: '#ef4444'}}
              disabled={!cancelReason || cancelling}
              onClick={() => {
                showConfirm(
                  '確認',
                  '本当にキャンセルしますか？この操作は取り消せません。',
                  async () => {
                    setCancelling(true);
                    try {
                      await axiosInstance.post(`/api/medichat_doctor/post_consult_reviews/${selectedSessionId}/admin_cancel`, {
                        cancellation_reason: cancelReason,
                        admin_note: cancelNote
                      });
                      showAlert('成功', '診察をキャンセルしました', 'success', async () => {
                        await loadList();
                        setSelectedSessionId(null);
                        setDetail(null);
                        setCancelModalOpen(false);
                        setCancelReason('');
                        setCancelNote('');
                        navigate('/doctor/review');
                      });
                    } catch (e) {
                      showAlert('エラー', e?.response?.data?.error || 'キャンセルに失敗しました');
                    } finally {
                      setCancelling(false);
                    }
                  },
                  'warning'
                );
              }}
            >
              {cancelling ? 'キャンセル中...' : 'キャンセル実行'}
            </button>
          </div>
        </div>
      </div>
    )}
    {/* 共通モーダル */}
    <AlertModal 
      open={alertModal.open}
      title={alertModal.title}
      message={alertModal.message}
      type={alertModal.type}
      iconType={alertModal.iconType}
      onConfirm={alertModal.onConfirm}
      onClose={() => {
        const callback = alertModal.onCloseCallback;
        setAlertModal({ open: false, title: '', message: '', onCloseCallback: null, type: 'alert', onConfirm: null, iconType: null });
        if (callback) callback();
      }}
    />
    </div>
  );
};

export default PostConsultReviewPage;
